import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarDays,
  ChevronLeft,
  ClipboardEdit,
  HeartHandshake,
  MapPin,
  Megaphone,
  Phone,
  RotateCcw,
  Smartphone,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import ChurchLogo from "../../../assets/logos/Church-Logo.png";
import ChurchIcon from "../../../assets/logos/Oikos Church Icon.png";
import InteractiveAnnouncementsIcon from "../../../assets/interactive-screen-icons/announcements.png";
import InteractiveConnectIcon from "../../../assets/interactive-screen-icons/connect.png";
import InteractiveEventsIcon from "../../../assets/interactive-screen-icons/events.png";
import InteractiveMembersIcon from "../../../assets/interactive-screen-icons/members.png";
import InteractivePrayerIcon from "../../../assets/interactive-screen-icons/prayer.png";
import InteractiveVisitIcon from "../../../assets/interactive-screen-icons/visit.png";
import MensDutiesIcon from "../../../assets/logos/Mens Duties Icon.png";
import {
  loadChurchLiveScreenStatus,
  loadPublicChurchLiveDisplay,
  normalizePublicChurchLiveDisplayRow,
  registerChurchLiveScreen,
  subscribeToChurchLiveDisplayControl,
  subscribeToPublicChurchLiveDisplay,
} from "../services/liveDisplayService";
import { loadGlobalStyles } from "../services/globalAdminService";
import { createPublicMensDuty } from "../services/mensDutiesService";

function getDeviceInfo() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.navigator.userAgent || "Browser";
}

function getChurchAppUrl(orgCode = "") {
  if (typeof window === "undefined") return "/church";
  const normalizedOrgCode = String(orgCode || "").trim();
  return normalizedOrgCode
    ? `${window.location.origin}/church/login/${encodeURIComponent(normalizedOrgCode)}`
    : `${window.location.origin}/church`;
}

function getQrUrl(url) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=18&format=png&data=${encodeURIComponent(url)}`;
}

const SCREEN_GROUP_SESSION_PREFIX = "oikos.church.live-display.requested-screen-group";
const CONTROLLER_PREVIEW_CHANNEL = "oikos.church.service-controller-preview";
const LIVE_DISPLAY_POLL_INTERVAL_MS = 3000;
const LOOP_DISPLAY_POLL_INTERVAL_MS = 750;
const SCREEN_STATUS_REFRESH_INTERVAL_MS = 5000;
const LIVE_CONTROL_SLIDE_LOCK_MS = 10000;
const LIVE_NEXT_SLIDE_KEYS = new Set(["ArrowRight", "PageDown", " ", "Spacebar", "Enter", "n", "N"]);
const LIVE_PREVIOUS_SLIDE_KEYS = new Set(["ArrowLeft", "PageUp", "Backspace", "p", "P"]);

function normalizeRequestedScreenGroup(value) {
  return String(value || "").trim().toLowerCase() === "interactive" ? "interactive" : "service";
}

function readScreenGroupFromSearch(search = "") {
  const params = new URLSearchParams(search || "");
  const value =
    params.get("screenGroup") ||
    params.get("screen_group") ||
    params.get("group") ||
    params.get("screen");

  return value ? normalizeRequestedScreenGroup(value) : "";
}

function getRequestedScreenGroup(displayCode = "") {
  if (typeof window === "undefined") {
    return "service";
  }

  const storageKey = `${SCREEN_GROUP_SESSION_PREFIX}.${displayCode || "default"}`;
  const hashSearchIndex = window.location.hash.indexOf("?");
  const requested =
    readScreenGroupFromSearch(window.location.search) ||
    (hashSearchIndex >= 0 ? readScreenGroupFromSearch(window.location.hash.slice(hashSearchIndex)) : "");

  if (requested) {
    try {
      window.sessionStorage.setItem(storageKey, requested);
    } catch (_error) {
      // Session storage is optional; the URL remains the source of truth.
    }
    return requested;
  }

  try {
    return normalizeRequestedScreenGroup(window.sessionStorage.getItem(storageKey));
  } catch (_error) {
    return "service";
  }
}

function getCachedChurchOrganizationBranding() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem("oikos.organization.church");
    const account = raw ? JSON.parse(raw) : null;

    return {
      name: account?.name || "",
      logoUrl: account?.logo_url || account?.logoUrl || "",
    };
  } catch (_error) {
    return {};
  }
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const SLEEP_WAKE_IDLE_MS = 60 * 1000;
const INTERACTIVE_RETURN_TO_SLIDES_IDLE_MS = 60 * 1000;
const INTERACTIVE_APP_DEFINITIONS = [
  {
    id: "members-directory",
    title: "Members Directory",
    subtitle: "Church families",
    menuLabel: "Members",
    slideItemId: "members-families-slide",
    icon: Users,
    image: InteractiveMembersIcon,
  },
  {
    id: "announcements",
    title: "Announcements",
    subtitle: "Church updates",
    menuLabel: "Announcements",
    slideItemId: "announcements-slide",
    icon: Megaphone,
    image: InteractiveAnnouncementsIcon,
  },
  {
    id: "events",
    title: "Events",
    subtitle: "Upcoming gatherings",
    menuLabel: "Events",
    slideItemId: "events-slide",
    icon: CalendarDays,
    image: InteractiveEventsIcon,
  },
  {
    id: "surrounding-events",
    title: "Surrounding Church Events",
    subtitle: "Shared by nearby churches",
    menuLabel: "Around Us",
    slideItemId: "surrounding-events-slide",
    icon: CalendarDays,
    image: InteractiveEventsIcon,
  },
  {
    id: "mens-duties",
    title: "Mens Duties",
    subtitle: "Sign up to serve",
    menuLabel: "Duties",
    slideItemId: "mens-duties-slide",
    icon: UserCheck,
    image: MensDutiesIcon,
  },
  {
    id: "preacher",
    title: "Preacher",
    subtitle: "Meet the preacher",
    menuLabel: "Preacher",
    slideItemId: "preacher-profile-slide",
    icon: User,
    image: ChurchLogo,
  },
  {
    id: "prayer-request",
    title: "Prayer Request",
    subtitle: "Share a request",
    menuLabel: "Prayer",
    icon: HeartHandshake,
    image: InteractivePrayerIcon,
  },
  {
    id: "visitor-log",
    title: "Visitor Log",
    subtitle: "Let us know you visited",
    menuLabel: "Visit",
    icon: ClipboardEdit,
    image: InteractiveVisitIcon,
  },
  {
    id: "phone-page",
    title: "Get Connected",
    subtitle: "Scan to open church sign in",
    menuLabel: "Connect",
    icon: Smartphone,
    image: InteractiveConnectIcon,
  },
];
const INTERACTIVE_SLIDE_APP_IDS = ["members-directory", "announcements", "events", "surrounding-events", "mens-duties", "preacher"];

function formatLiveServiceDate() {
  return `${new Date().toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  })}: Service`;
}

function formatLiveClockTime(timestamp = Date.now()) {
  return new Date(timestamp).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatServiceTimeLabel(time = "") {
  const [hourValue, minuteValue] = String(time || "").split(":");
  const hour = Number(hourValue);
  const minute = Number(minuteValue);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return "";
  }

  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function getSundayMorningServiceTime(serviceTimes = []) {
  const times = Array.isArray(serviceTimes) ? serviceTimes : [];
  const sundayMorning =
    times.find((item) => /sunday/i.test(item?.name || "") && /morning/i.test(item?.name || "")) ||
    times.find((item) => /morning/i.test(item?.name || "")) ||
    times[0];

  if (!sundayMorning?.time) {
    return "";
  }

  const formattedTime = formatServiceTimeLabel(sundayMorning.time);
  return formattedTime ? `${sundayMorning.name || "Sunday Morning Service"}: ${formattedTime}` : "";
}

function getUpcomingSundayOptions(count = 8) {
  const today = new Date();
  const firstSunday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  firstSunday.setDate(firstSunday.getDate() + ((7 - firstSunday.getDay()) % 7));

  return Array.from({ length: count }, (_item, index) => {
    const date = new Date(firstSunday);
    date.setDate(firstSunday.getDate() + index * 7);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const label = date.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });

    return { value, label };
  });
}

function isServiceClockSlide(item = {}) {
  return (
    item?.sourceType === "clock-slide" ||
    item?.source_type === "clock-slide" ||
    (
      item?.itemType === "custom_slide" &&
      String(item?.title || "").trim().toLowerCase() === "clock"
    )
  );
}

const INTERACTIVE_MENU_MOTION_CSS = `
  @keyframes interactiveMenuLogoFadeIn {
    from {
      opacity: 0;
      transform: translateY(16px) scale(0.96);
      filter: blur(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
      filter: blur(0);
    }
  }

  @keyframes interactiveMenuTileFloatIn {
    from {
      opacity: 0;
      transform: translate(var(--entry-x, 0), var(--entry-y, 40px)) scale(0.94);
    }
    to {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
  }

  @keyframes interactiveMenuTileWobble {
    0% { transform: translateY(-3px) scale(1.012); }
    100% { transform: translateY(-3px) scale(1.012); }
  }

  .interactive-church-logo {
    animation: interactiveMenuLogoFadeIn 760ms cubic-bezier(.2,.92,.2,1) 90ms both;
  }

  .interactive-church-app-button {
    animation: interactiveMenuTileFloatIn 620ms cubic-bezier(.2,.92,.2,1) both;
    transition: transform 180ms ease, filter 180ms ease;
    will-change: transform;
  }

  .interactive-church-app-button:hover {
    animation:
      interactiveMenuTileFloatIn 520ms cubic-bezier(.2,.92,.2,1) both,
      interactiveMenuTileWobble 560ms ease 520ms both;
    filter: brightness(1.015);
  }

  .interactive-church-app-button:active {
    transform: translateY(-1px) scale(0.995);
  }

  @media (max-width: 1180px) {
    .interactive-church-menu-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
  }

  @media (max-width: 860px) {
    .interactive-church-menu-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      padding-left: 18px !important;
      padding-right: 18px !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .interactive-church-logo,
    .interactive-church-app-button,
    .interactive-church-app-button:hover {
      animation: none;
      transform: none;
    }
  }
`;

const INTERACTIVE_TOUCH_PROMPT_CSS = `
  @keyframes interactiveTouchPromptHop {
    0%, 68%, 100% {
      opacity: 0.42;
      transform: translate(-50%, 0);
    }
    12% {
      opacity: 0.86;
      transform: translate(-50%, -10px);
    }
    24% {
      opacity: 0.58;
      transform: translate(-50%, 0);
    }
    34% {
      opacity: 0.74;
      transform: translate(-50%, -5px);
    }
    44% {
      opacity: 0.48;
      transform: translate(-50%, 0);
    }
  }

  @keyframes interactiveTouchPromptGlow {
    0%, 100% {
      opacity: 0.22;
      transform: scaleX(0.74);
    }
    22% {
      opacity: 0.42;
      transform: scaleX(1);
    }
  }

  .interactive-touch-prompt {
    animation: interactiveTouchPromptHop 3.6s ease-in-out infinite;
  }

  .interactive-touch-prompt::after {
    animation: interactiveTouchPromptGlow 3.6s ease-in-out infinite;
    background: linear-gradient(90deg, transparent, rgba(95, 125, 77, 0.34), transparent);
    border-radius: 999px;
    bottom: -8px;
    content: "";
    height: 1px;
    left: 12%;
    position: absolute;
    right: 12%;
    transform-origin: center;
  }

  @media (prefers-reduced-motion: reduce) {
    .interactive-touch-prompt,
    .interactive-touch-prompt::after {
      animation: none;
    }
  }
`;

function getInteractiveAppEntryMotion(index = 0) {
  const column = index % 3;
  const row = Math.floor(index / 3);
  const entryX = column === 0 ? "-44vw" : column === 2 ? "44vw" : "0";
  const entryY = row === 0 ? "-32vh" : row >= 2 ? "36vh" : "44px";
  const wobbleSign = index % 2 === 0 ? -1 : 1;

  return {
    "--entry-x": entryX,
    "--entry-y": entryY,
    "--hover-rotate-a": `${wobbleSign * 1.25}deg`,
    "--hover-rotate-b": `${wobbleSign * -0.95}deg`,
    "--hover-rotate-c": `${wobbleSign * 0.45}deg`,
    animationDelay: `${Math.min(index * 55, 420)}ms`,
  };
}

function timeToMinutes(value = "") {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/);
  if (!match) return 0;

  return (Number(match[1]) || 0) * 60 + (Number(match[2]) || 0);
}

function isSleepScheduleActive(schedule = {}, date = new Date()) {
  if (schedule?.enabled !== true) {
    return false;
  }

  const days = Array.isArray(schedule.days) ? schedule.days : [];
  if (days.length === 0) {
    return false;
  }

  const todayIndex = date.getDay();
  const yesterdayIndex = (todayIndex + 6) % 7;
  const todayKey = WEEKDAY_KEYS[todayIndex];
  const yesterdayKey = WEEKDAY_KEYS[yesterdayIndex];
  const nowMinutes = date.getHours() * 60 + date.getMinutes();
  const startMinutes = timeToMinutes(schedule.startTime || "22:00");
  const endMinutes = timeToMinutes(schedule.endTime || "06:00");

  if (startMinutes === endMinutes) {
    return days.includes(todayKey);
  }

  if (startMinutes < endMinutes) {
    return days.includes(todayKey) && nowMinutes >= startMinutes && nowMinutes < endMinutes;
  }

  return (
    (days.includes(todayKey) && nowMinutes >= startMinutes) ||
    (days.includes(yesterdayKey) && nowMinutes < endMinutes)
  );
}

function getOneLineTitleStyle(title = "") {
  const characterCount = Math.max(String(title || "").trim().length, 1);
  const estimatedViewportSize = Math.max(
    1.4,
    Math.min(15, 92 / (characterCount * 0.54))
  );

  return {
    fontSize: `clamp(28px, min(${estimatedViewportSize.toFixed(2)}vw, 20vh), 190px)`,
  };
}

function getLiveServiceSongTitleStyle(title = "") {
  const characterCount = Math.max(String(title || "").trim().length, 1);
  const wordCount = String(title || "").trim().split(/\s+/).filter(Boolean).length;
  const estimatedViewportSize = Math.max(
    2.2,
    Math.min(8.2, 122 / (characterCount * 0.42))
  );
  const maxBlockHeight = characterCount > 74 || wordCount > 10
    ? "50vh"
    : characterCount > 48 || wordCount > 7
      ? "43vh"
      : "36vh";

  return {
    fontSize: `clamp(28px, min(${estimatedViewportSize.toFixed(2)}vw, 11vh), 138px)`,
    maxHeight: maxBlockHeight,
  };
}

function getLiveServiceSongNumberStyle(title = "") {
  const characterCount = Math.max(String(title || "").trim().length, 1);

  if (characterCount > 74) {
    return {
      fontSize: "clamp(34px, min(5.4vw, 7vh), 86px)",
      marginTop: "clamp(12px, 2vh, 26px)",
    };
  }

  if (characterCount > 48) {
    return {
      fontSize: "clamp(40px, min(6.4vw, 9vh), 108px)",
      marginTop: "clamp(14px, 2.4vh, 34px)",
    };
  }

  return {};
}

function getInvitationSongParts(body = "") {
  const text = String(body || "").trim();
  const match = text.match(/^(#?\d+[A-Za-z]?)\s+(.+)$/);

  if (!match) {
    return {
      number: text,
      name: "",
    };
  }

  return {
    number: match[1].startsWith("#") ? match[1] : `#${match[1]}`,
    name: match[2],
  };
}

function getOneLineVerseReferenceStyle(reference = "") {
  const characterCount = Math.max(String(reference || "").trim().length, 1);
  const estimatedViewportSize = Math.max(
    6.2,
    Math.min(18, 122 / (characterCount * 0.54))
  );

  return {
    fontSize: `clamp(72px, min(${estimatedViewportSize.toFixed(2)}vw, 15vh), 220px)`,
  };
}

function getHymnListFitStyles(entryCount = 0, compact = false) {
  if (compact) {
    return {};
  }

  const count = Math.max(1, Math.min(6, Number(entryCount) || 1));
  const rowVh = Math.max(5.6, Math.min(8.4, 43 / count));
  const numberVh = Math.max(2.6, Math.min(5.2, 27 / count));
  const titleVh = Math.max(2.2, Math.min(4.8, 24 / count));
  const gapVh = Math.max(0.7, Math.min(1.25, 7 / count));

  return {
    list: {
      gap: `clamp(6px, ${gapVh.toFixed(2)}vh, 14px)`,
      marginTop: "clamp(10px, 1.6vh, 24px)",
      maxHeight: "calc(100dvh - clamp(250px, 34vh, 390px))",
      overflow: "hidden",
    },
    row: {
      minHeight: `clamp(48px, ${rowVh.toFixed(2)}vh, 92px)`,
      padding: "clamp(5px, 0.72vh, 10px) 0",
    },
    number: {
      fontSize: `clamp(21px, min(3.4vw, ${numberVh.toFixed(2)}vh), 56px)`,
    },
    title: {
      fontSize: `clamp(18px, min(2.9vw, ${titleVh.toFixed(2)}vh), 50px)`,
      lineHeight: 1.08,
    },
    label: {
      marginTop: "clamp(3px, 0.42vh, 7px)",
    },
  };
}

function normalizeVerseTextSpacing(text = "") {
  return String(text || "")
    .replace(/\s+/g, " ")
    .replace(/([.!?])(["'”’)]*)(?=[A-Z])/g, "$1$2 ")
    .replace(/([,;:])(?=\S)/g, "$1 ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

function getLiveVerseTextStyle(item = {}) {
  const verseLines = getLiveVerseLines(item);
  const passageText = verseLines
    .map((line) => `${line.number || ""} ${line.text || ""}`.trim())
    .join(" ");
  const characterCount = Math.max(passageText.length, 1);
  const longestLineLength = Math.max(
    1,
    ...verseLines.map((line) => String(line?.text || "").length)
  );
  const estimatedLineCount = Math.max(
    verseLines.length,
    Math.ceil(characterCount / 72),
    Math.ceil(longestLineLength / 58)
  );
  const isLongPassage = verseLines.length > 5;
  const verseCountPenalty = Math.max(0, verseLines.length - 5) * 0.18;
  const estimatedViewportSize = Math.max(
    isLongPassage ? 1.85 : 2.3,
    Math.min(isLongPassage ? 4.05 : 4.9, (44 / Math.max(estimatedLineCount, 1)) - verseCountPenalty)
  );

  return {
    fontSize: `clamp(${isLongPassage ? 25 : 30}px, min(${estimatedViewportSize.toFixed(2)}vw, ${estimatedViewportSize.toFixed(2)}vh), 82px)`,
    gap: isLongPassage ? "clamp(6px, 0.75vh, 13px)" : undefined,
    justifyContent: verseLines.length <= 3 ? "center" : undefined,
    lineHeight: isLongPassage ? 1.04 : undefined,
  };
}

function getContentSourceLabel(item = {}, isLive = false) {
  if (!isLive) return "Pre Service Loop";

  const sourceType = String(item?.sourceType || "").toLowerCase();
  const itemType = String(item?.itemType || item?.type || "").toLowerCase();
  const itemId = String(item?.id || "").toLowerCase();
  const parentId = String(item?.parentId || item?.parent_id || "").toLowerCase();
  const sourceApp = String(item?.sourceApp || item?.source_app || "").toLowerCase();

  if (sourceApp.includes("hymn") || itemType.includes("hymn")) return "Hymns";
  if (sourceApp.includes("sermon") || sourceType.includes("sermon")) return "Sermon";
  if (itemType === "title_slide" || itemType === "verse") return "Sermon";
  if (itemId.includes("sermon") || parentId.includes("sermon")) return "Sermon";
  if (itemType === "verse" && isLive) return "Sermon";
  if (itemId.includes("announcement")) return "Announcements";
  if (itemId.includes("event")) return "Events";
  return "Live";
}

function getInteractiveApps(display = {}) {
  const appConfig = display?.interactiveConfig?.apps || {};
  return INTERACTIVE_APP_DEFINITIONS.filter((app) => appConfig[app.id] !== false);
}

function getInteractiveLoopItem(display = {}, appId = "") {
  const app = INTERACTIVE_APP_DEFINITIONS.find((definition) => definition.id === appId);
  if (!app?.slideItemId) return null;

  return (display?.loopItems || []).find((item) => item?.id === app.slideItemId) || null;
}

function getInteractiveSlideItems(display = {}) {
  const enabledApps = getInteractiveApps(display);
  const slides = INTERACTIVE_SLIDE_APP_IDS
    .map((appId) => {
      if (!enabledApps.some((app) => app.id === appId)) return null;

      const app = INTERACTIVE_APP_DEFINITIONS.find((definition) => definition.id === appId);
      const loopItem = getInteractiveLoopItem(display, appId);

      return {
        ...(loopItem || {}),
        id: `interactive-${appId}`,
        appId,
        title: loopItem?.title || app?.title || "Welcome",
        subtitle: loopItem?.subtitle || app?.subtitle || "",
        body: loopItem?.body || "",
        entries: Array.isArray(loopItem?.entries) ? loopItem.entries : [],
        memberEntries: Array.isArray(loopItem?.memberEntries) ? loopItem.memberEntries : [],
      };
    })
    .filter(Boolean);

  if (slides.length > 0) return slides;

  return [
    {
      id: "interactive-welcome",
      appId: "welcome",
      title: display?.organizationName || "Welcome",
      subtitle: "Interactive Foyer",
      body: "Touch the screen to open the menu.",
      entries: [],
    },
  ];
}

function getVerseNumberFromReference(reference = "") {
  const match = String(reference || "").match(/:(\d{1,3})(?:-\d{1,3})?(?:\D*)$/);
  return match ? match[1] : "";
}

function getDisplayVerseNumber(item = {}) {
  const explicitNumber = String(item.verseNumber || "").trim();

  if (explicitNumber && explicitNumber !== "0") {
    return explicitNumber;
  }

  return getVerseNumberFromReference(item.reference || item.title || "");
}

function splitEmbeddedVerseLines(text = "") {
  const value = normalizeVerseTextSpacing(text);
  const matches = Array.from(value.matchAll(/(?:^|\s)(\d{1,3}[A-Za-z]?)(?=[A-Z"“])/g));

  if (matches.length <= 1) return [];

  return matches
    .map((match, index) => {
      const numberStart = match.index + match[0].length - match[1].length;
      const textStart = numberStart + match[1].length;
      const nextMatch = matches[index + 1];
      const textEnd = nextMatch ? nextMatch.index : value.length;

      return {
        number: match[1],
        text: normalizeVerseTextSpacing(value.slice(textStart, textEnd)),
      };
    })
    .filter((line) => line.text);
}

function getLiveVerseLines(item = {}) {
  const textLines = String(item.text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const fallbackVerseNumber = getDisplayVerseNumber(item);

  if (String(item.verseNumber || "").trim() && String(item.verseNumber || "").trim() !== "0") {
    const segmentedLines = splitEmbeddedVerseLines(textLines.join(" ") || item.text || "");

    if (segmentedLines.length > 1) {
      return segmentedLines;
    }

    return [
      {
        number: getDisplayVerseNumber(item),
        text: normalizeVerseTextSpacing(textLines.join("\n\n") || item.text || ""),
      },
    ];
  }

  return textLines.map((line, index) => {
    const match = line.match(/^(\d+[a-zA-Z]?)[\s.:)]+(.+)$/);

    if (!match) {
      return {
        number: index === 0 ? fallbackVerseNumber : "",
        text: normalizeVerseTextSpacing(line),
      };
    }

    return {
      number: match[1],
      text: normalizeVerseTextSpacing(match[2]),
    };
  });
}

function getVerseTextSegments(text = "") {
  const value = String(text || "");
  const verseNumberPattern = /(^|\s)(\d{1,3})(?=[A-Za-z"“])/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = verseNumberPattern.exec(value)) !== null) {
    const numberStart = match.index + match[1].length;
    const numberEnd = numberStart + match[2].length;

    if (numberStart > lastIndex) {
      segments.push({ type: "text", value: value.slice(lastIndex, numberStart) });
    }

    segments.push({ type: "number", value: match[2] });
    lastIndex = numberEnd;
  }

  if (lastIndex < value.length) {
    segments.push({ type: "text", value: value.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: "text", value }];
}

function renderLoopEntries(item, compact = false) {
  const entries = Array.isArray(item?.entries) ? item.entries.slice(0, 6) : [];
  const showEntryLabel = !["announcements-slide", "events-slide", "surrounding-events-slide"].includes(item?.id);
  const isHymnList = item?.id === "current-hymns-slide" || item?.type === "current-hymns";
  const isAnnouncementList = item?.id === "announcements-slide" || item?.type === "announcement";
  const isEventList = item?.id === "events-slide" || item?.id === "surrounding-events-slide" || item?.type === "event";
  const isMensDutiesList = item?.id === "mens-duties-slide" || item?.type === "mens-duties";
  const hymnFitStyles = getHymnListFitStyles(entries.length, compact);

  if (entries.length === 0) {
    return null;
  }

  if (isHymnList) {
    return (
      <div style={compact ? styles.previewHymnEntryList : { ...styles.hymnEntryList, ...hymnFitStyles.list }}>
        {entries.map((entry, index) => (
          <div
            key={entry.id || `${item?.id || "entry"}-${index}`}
            style={compact ? styles.previewHymnEntryRow : { ...styles.hymnEntryRow, ...hymnFitStyles.row }}
          >
            <div style={compact ? styles.previewHymnEntryNumber : { ...styles.hymnEntryNumber, ...hymnFitStyles.number }}>{entry.title || `#${index + 1}`}</div>
            <div style={compact ? styles.previewHymnEntryMain : styles.hymnEntryMain}>
              <div style={compact ? styles.previewHymnEntryTitle : { ...styles.hymnEntryTitle, ...hymnFitStyles.title }}>
                {entry.body || "Selected hymn"}
              </div>
              {entry.label ? (
                <div style={compact ? styles.previewHymnEntryLabel : { ...styles.hymnEntryLabel, ...hymnFitStyles.label }}>{entry.label}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isAnnouncementList) {
    return (
      <div style={compact ? styles.previewAnnouncementList : styles.announcementList}>
        {entries.map((entry, index) => (
          <div
            key={entry.id || `${item?.id || "announcement"}-${index}`}
            style={compact ? styles.previewAnnouncementRowNoNumber : styles.announcementRowNoNumber}
          >
            <div style={styles.announcementContent}>
              <div style={compact ? styles.previewAnnouncementTitle : styles.announcementTitle}>
                {entry.title || "Church Update"}
              </div>
              {entry.body ? (
                <div style={compact ? styles.previewAnnouncementBody : styles.announcementBody}>
                  {entry.body}
                </div>
              ) : null}
            </div>
          </div>
        ))}
        {Array.isArray(item?.entries) && item.entries.length > entries.length ? (
          <div style={compact ? styles.previewAnnouncementMoreText : styles.announcementMoreText}>
            + {item.entries.length - entries.length} more
          </div>
        ) : null}
      </div>
    );
  }

  if (isEventList) {
    return (
      <div style={compact ? styles.previewAnnouncementList : styles.announcementList}>
        {entries.map((entry, index) => (
          <div
            key={entry.id || `${item?.id || "event"}-${index}`}
            style={compact ? styles.previewAnnouncementRowNoNumber : styles.announcementRowNoNumber}
          >
            <div style={styles.announcementContent}>
              <div style={compact ? styles.previewAnnouncementTitle : styles.announcementTitle}>
                {entry.title || "Church Event"}
              </div>
              {entry.subtitle ? (
                <div style={compact ? styles.previewAnnouncementBody : styles.announcementBody}>
                  {entry.subtitle}
                </div>
              ) : null}
              {entry.body ? (
                <div style={compact ? styles.previewAnnouncementBody : styles.announcementBody}>
                  {entry.body}
                </div>
              ) : null}
            </div>
          </div>
        ))}
        {Array.isArray(item?.entries) && item.entries.length > entries.length ? (
          <div style={compact ? styles.previewAnnouncementMoreText : styles.announcementMoreText}>
            + {item.entries.length - entries.length} more
          </div>
        ) : null}
      </div>
    );
  }

  if (isMensDutiesList) {
    return (
      <div style={compact ? styles.previewAnnouncementList : styles.announcementList}>
        {entries.map((entry, index) => (
          <div
            key={entry.id || `${item?.id || "duty"}-${index}`}
            style={compact ? styles.previewAnnouncementRowNoNumber : styles.announcementRowNoNumber}
          >
            <div style={styles.announcementContent}>
              <div style={compact ? styles.previewAnnouncementTitle : styles.announcementTitle}>
                {entry.title || "Duty"}
              </div>
              {entry.subtitle ? (
                <div style={compact ? styles.previewAnnouncementBody : styles.announcementBody}>
                  {entry.subtitle}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={compact ? styles.previewEntryList : styles.entryList}>
      {entries.map((entry, index) => (
        <div key={entry.id || `${item?.id || "entry"}-${index}`} style={compact ? styles.previewEntryCard : styles.entryCard}>
          {showEntryLabel ? (
            <div style={compact ? styles.previewEntryLabel : styles.entryLabel}>
              {entry.label || item?.subtitle || item?.type || "Update"}
            </div>
          ) : null}
          <div style={compact ? styles.previewEntryTitle : styles.entryTitle}>
            {entry.title || "Church Update"}
          </div>
          {entry.subtitle ? (
            <div style={compact ? styles.previewEntryMeta : styles.entryMeta}>{entry.subtitle}</div>
          ) : null}
          {entry.body ? (
            <div style={compact ? styles.previewEntryBody : styles.entryBody}>{entry.body}</div>
          ) : null}
        </div>
      ))}
      {Array.isArray(item?.entries) && item.entries.length > entries.length ? (
        <div style={compact ? styles.previewMoreText : styles.moreText}>
          + {item.entries.length - entries.length} more
        </div>
      ) : null}
    </div>
  );
}

function LiveDisplayPreparingScreen() {
  return (
    <div style={styles.preparingPage}>
      <style>
        {`
          @keyframes oikosPreparingCore {
            0%, 100% {
              transform: scale(1);
              box-shadow:
                0 0 30px rgba(255,255,255,0.28),
                0 0 76px rgba(var(--color-primary-rgb),0.42);
            }
            50% {
              transform: scale(1.055);
              box-shadow:
                0 0 48px rgba(255,255,255,0.5),
                0 0 132px rgba(var(--color-primary-rgb),0.72);
            }
          }

          @keyframes oikosPreparingOrbit {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes oikosPreparingRing {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.34);
            }
            28% {
              opacity: 0.76;
            }
            100% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(1.25);
            }
          }

          @keyframes oikosPreparingText {
            0%, 100% { opacity: 0.62; }
            50% { opacity: 1; }
          }
        `}
      </style>
      <div style={styles.preparingIconWrap}>
        <div style={{ ...styles.preparingRing, ...styles.preparingRingOne }} />
        <div style={{ ...styles.preparingRing, ...styles.preparingRingTwo }} />
        <div style={styles.preparingOrbit}>
          <span style={styles.preparingOrbitDot} />
        </div>
        <div style={styles.preparingCore}>
          <img src={ChurchIcon} alt="Oikos Church" style={styles.preparingIcon} />
        </div>
      </div>
      <div style={styles.preparingTitle}>Preparing your display.</div>
      <div style={styles.preparingDetail}>Syncing the live screen.</div>
    </div>
  );
}

export default function ChurchLiveDisplayViewerPage() {
  const { displayCode, interactiveAppId } = useParams();
  const navigate = useNavigate();
  const [screenCode, setScreenCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [displayData, setDisplayData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [registeredScreen, setRegisteredScreen] = useState(null);
  const [interactiveMenuOpen, setInteractiveMenuOpen] = useState(false);
  const [interactiveFormNotice, setInteractiveFormNotice] = useState("");
  const [globalStyles, setGlobalStyles] = useState({});
  const [mensDutySignup, setMensDutySignup] = useState(() => ({
    dutyDate: getUpcomingSundayOptions(1)[0]?.value || "",
    dutyName: "",
    assigneeName: "",
    note: "",
  }));
  const [selectedInteractiveFamilyId, setSelectedInteractiveFamilyId] = useState("");
  const [localLiveSlideIndex, setLocalLiveSlideIndex] = useState(null);
  const [scheduleTick, setScheduleTick] = useState(() => Date.now());
  const [sleepAwakeUntil, setSleepAwakeUntil] = useState(0);
  const previousSlideMetaRef = useRef({ isLiveHymn: false, key: "" });
  const lastRemoteLiveSlideIndexRef = useRef(0);
  const sleepWakeTimerRef = useRef(null);
  const registeredScreenRef = useRef(null);
  const liveControlSlideLockRef = useRef({ index: null, expiresAt: 0 });

  const hasDisplayCode = Boolean(String(displayCode || "").trim());
  const normalizedInteractiveAppId = String(interactiveAppId || "").trim().toLowerCase();
  const isControllerPreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search || "");
    return params.get("controllerPreview") === "1";
  }, []);
  const displayPollState = isControllerPreview ? "" : displayData?.display?.state;

  useEffect(() => {
    registeredScreenRef.current = registeredScreen;
  }, [registeredScreen]);

  const applyLiveControlSlideLock = useCallback((nextData, currentData = null) => {
    const nextDisplay = nextData?.display || null;
    const lock = liveControlSlideLockRef.current;

    if (!nextDisplay) {
      liveControlSlideLockRef.current = { index: null, expiresAt: 0 };
      return nextData;
    }

    if (nextDisplay.state !== "live") {
      liveControlSlideLockRef.current = { index: null, expiresAt: 0 };
      return nextData;
    }

    if (lock.index === null || Date.now() > lock.expiresAt) {
      return nextData;
    }

    const remoteIndex = Math.max(Number(nextDisplay.currentSlideIndex) || 0, 0);
    if (remoteIndex === lock.index) {
      return nextData;
    }

    return {
      ...(nextData || currentData || {}),
      display: {
        ...(nextDisplay || {}),
        currentSlideIndex: lock.index,
        state: "live",
        isLive: true,
      },
      screens: nextData?.screens || currentData?.screens || [],
    };
  }, []);

  useEffect(() => {
    if (!hasDisplayCode) {
      setLoading(false);
      setError("");
      setDisplayData(null);
      return undefined;
    }

    let mounted = true;

    async function loadDisplay() {
      if (!displayData) {
        setLoading(true);
      }
      setError("");

      try {
        const result = await loadPublicChurchLiveDisplay(displayCode);

        if (!mounted) return;

        if (!result?.display) {
          setError("This live display link is not active yet.");
          setDisplayData(null);
          setLoading(false);
          return;
        }

        setDisplayData((currentData) => applyLiveControlSlideLock(result, currentData));
        setLoading(false);

        const currentRegisteredScreen = registeredScreenRef.current;
        if (
          !isControllerPreview &&
          (!currentRegisteredScreen?.screenCode ||
            currentRegisteredScreen?.displayId !== result.display?.id)
        ) {
          try {
            const requestedScreenGroup = getRequestedScreenGroup(displayCode);
            const nextScreen = await registerChurchLiveScreen(
              result.display,
              requestedScreenGroup === "interactive" ? "Interactive screen" : "Live screen",
              getDeviceInfo(),
              requestedScreenGroup
            );
            setRegisteredScreen(nextScreen);
          } catch (screenError) {
            console.error("Live display screen registration error:", screenError);
          }
        }
      } catch (loadError) {
        console.error("Public live display load error:", loadError);

        if (!mounted) return;

        setError(loadError?.message || "Could not load the live display.");
        setDisplayData(null);
        setLoading(false);
      }
    }

    loadDisplay();

    const interval = isControllerPreview
      ? null
      : window.setInterval(
          loadDisplay,
          displayData?.display?.state === "live" ? LIVE_DISPLAY_POLL_INTERVAL_MS : LOOP_DISPLAY_POLL_INTERVAL_MS
        );

    return () => {
      mounted = false;
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [applyLiveControlSlideLock, displayCode, displayPollState, hasDisplayCode, isControllerPreview]);

  useEffect(() => {
    if (!hasDisplayCode || isControllerPreview) {
      return undefined;
    }

    return subscribeToPublicChurchLiveDisplay(displayCode, (row) => {
      setDisplayData((currentData) => {
        const nextDisplay = normalizePublicChurchLiveDisplayRow(row, currentData?.display || {});
        if (!nextDisplay) return currentData;
        const currentSlides = Array.isArray(currentData?.display?.slides)
          ? currentData.display.slides
          : [];
        const nextSlides = Array.isArray(nextDisplay.slides) ? nextDisplay.slides : [];

        return applyLiveControlSlideLock({
          ...(currentData || {}),
          display: {
            ...nextDisplay,
            slides:
              nextDisplay.state === "live" && nextSlides.length === 0 && currentSlides.length > 0
                ? currentSlides
                : nextSlides,
          },
          screens: currentData?.screens || [],
        }, currentData);
      });
      setLoading(false);
      setError("");
    });
  }, [applyLiveControlSlideLock, displayCode, hasDisplayCode, isControllerPreview]);

  useEffect(() => {
    if (!hasDisplayCode || isControllerPreview) {
      return undefined;
    }

    return subscribeToChurchLiveDisplayControl(displayCode, (message) => {
      if (message?.type !== "slide-index") return;

      const nextIndex = Math.max(Number(message.currentSlideIndex) || 0, 0);
      liveControlSlideLockRef.current = {
        index: nextIndex,
        expiresAt: Date.now() + LIVE_CONTROL_SLIDE_LOCK_MS,
      };
      lastRemoteLiveSlideIndexRef.current = nextIndex;
      setDisplayData((currentData) => {
        if (!currentData?.display) return currentData;

        return {
          ...currentData,
          display: {
            ...currentData.display,
            state: message.state || currentData.display.state || "live",
            isLive: message.isLive !== false,
            selectedServiceItemId:
              message.selectedServiceItemId || currentData.display.selectedServiceItemId,
            currentSlideIndex: nextIndex,
            slides: Array.isArray(message.liveSlides) && message.liveSlides.length
              ? message.liveSlides
              : currentData.display.slides,
          },
          screens: currentData.screens || [],
        };
      });
      setLocalLiveSlideIndex(null);
      setActiveIndex(nextIndex);
      setLoading(false);
      setError("");
    });
  }, [displayCode, hasDisplayCode, isControllerPreview]);

  useEffect(() => {
    if (!isControllerPreview || typeof window === "undefined" || !window.BroadcastChannel) {
      return undefined;
    }

    const channel = new window.BroadcastChannel(CONTROLLER_PREVIEW_CHANNEL);
    channel.onmessage = (event) => {
      const message = event.data || {};
      if (message.type !== "display-update" || !message.display) return;

      setDisplayData((currentData) => ({
        ...(currentData || {}),
        display: {
          ...(currentData?.display || {}),
          ...message.display,
        },
        screens: currentData?.screens || [],
      }));
      setLoading(false);
      setError("");
    };

    return () => {
      channel.close();
    };
  }, [isControllerPreview]);

  useEffect(() => {
    let mounted = true;

    async function loadStyles() {
      try {
        const nextStyles = await loadGlobalStyles();
        if (mounted) setGlobalStyles(nextStyles || {});
      } catch (stylesError) {
        console.error("Global interactive styles load error:", stylesError);
      }
    }

    loadStyles();

    return () => {
      mounted = false;
    };
  }, []);

  function handleScreenCodeSubmit(event) {
    event.preventDefault();
    const nextCode = screenCode.trim();
    if (!nextCode) return;

    navigate(`/live/${encodeURIComponent(nextCode)}`, { replace: true });
  }

  const currentScreen =
    (registeredScreen?.screenCode &&
      (displayData?.screens || []).find((screen) => screen.screenCode === registeredScreen.screenCode)) ||
    registeredScreen;
  const isIdentifyMode = currentScreen?.status === "identify";
  const isInteractiveScreen = currentScreen?.screenGroup === "interactive";
  const interactiveApps = useMemo(
    () => getInteractiveApps(displayData?.display || {}),
    [displayData?.display?.interactiveConfig]
  );
  const selectedInteractiveApp =
    INTERACTIVE_APP_DEFINITIONS.find((app) => app.id === normalizedInteractiveAppId) || null;
  const selectedInteractiveAppEnabled =
    Boolean(selectedInteractiveApp) &&
    interactiveApps.some((app) => app.id === selectedInteractiveApp.id);
  const churchLoginCode =
    displayData?.display?.organizationLoginCode ||
    displayData?.display?.churchCode ||
    displayData?.display?.inviteCode ||
    displayData?.display?.accountId ||
    "";
  const churchAppUrl = getChurchAppUrl(churchLoginCode);
  const churchAppQrUrl = getQrUrl(churchAppUrl);
  const interactiveSlideItems = useMemo(
    () => getInteractiveSlideItems(displayData?.display || {}),
    [displayData?.display?.interactiveConfig, displayData?.display?.loopItems]
  );

  const contentItems = useMemo(() => {
    if (!displayData?.display) {
      return [];
    }

    if (isInteractiveScreen && !normalizedInteractiveAppId) {
      return interactiveSlideItems;
    }

    if (displayData.display.state === "live") {
      return displayData.display.slides || [];
    }

    return (displayData.display.loopItems || []).filter(
      (item) =>
        item?.isVisible !== false &&
        item?.itemType !== "hymn" &&
        item?.sourceApp !== "hymn"
    );
  }, [
    displayData?.display?.loopItems,
    displayData?.display?.slides,
    displayData?.display?.state,
    interactiveSlideItems,
    isInteractiveScreen,
    normalizedInteractiveAppId,
  ]);

  const isLive = displayData?.display?.state === "live";
  const remoteLiveSlideIndex = displayData?.display?.currentSlideIndex || 0;
  const liveCurrentSlideIndex = localLiveSlideIndex ?? remoteLiveSlideIndex;
  const shouldAutoAdvanceLoop =
    !isLive && contentItems.length > 1;
  const activeDurationSeconds = Math.max(
    Number(contentItems[activeIndex]?.durationSeconds) ||
      displayData?.display?.loopIntervalSeconds ||
      6,
    3
  );
  const forcedSleepActive = displayData?.display?.state === "sleep";
  const sleepWindowActive = Boolean(
    displayData?.display &&
      isSleepScheduleActive(displayData.display.sleepSchedule, new Date(scheduleTick))
  );
  const shouldShowSleepScreen = (forcedSleepActive || sleepWindowActive) && sleepAwakeUntil <= Date.now();
  const liveServiceDateLabel = useMemo(() => formatLiveServiceDate(), [scheduleTick]);
  const activeContentItem = contentItems[activeIndex] || null;
  const isActiveLiveClockSlide = isLive && isServiceClockSlide(activeContentItem);
  const activeTransitionKey = isLive
    ? "live-service-slide"
    : `${displayData?.display?.state || "empty"}-${activeIndex}-${activeContentItem?.id || "empty"}`;
  const isActiveLiveHymn = isLive && activeContentItem?.itemType === "hymn";
  const isHymnToHymnTransition =
    previousSlideMetaRef.current.isLiveHymn &&
    isActiveLiveHymn &&
    previousSlideMetaRef.current.key !== activeTransitionKey;

  useEffect(() => {
    if (!isLive) {
      setLocalLiveSlideIndex(null);
      lastRemoteLiveSlideIndexRef.current = 0;
      return;
    }

    if (remoteLiveSlideIndex !== lastRemoteLiveSlideIndexRef.current) {
      lastRemoteLiveSlideIndexRef.current = remoteLiveSlideIndex;
      setLocalLiveSlideIndex(null);
    }
  }, [isLive, remoteLiveSlideIndex]);

  useEffect(() => {
    if (isLive) {
      setActiveIndex(liveCurrentSlideIndex);
      return;
    }

    setActiveIndex(0);
  }, [contentItems.length, isLive, liveCurrentSlideIndex]);

  const goToLocalLiveSlide = useCallback((direction) => {
    if (!isLive || isInteractiveScreen || contentItems.length === 0) return;

    setActiveIndex((current) => {
      const safeIndex = Math.min(
        Math.max(current + direction, 0),
        Math.max(contentItems.length - 1, 0)
      );
      setLocalLiveSlideIndex(safeIndex);
      return safeIndex;
    });
  }, [contentItems.length, isInteractiveScreen, isLive]);

  const handleLiveSlidePointerDown = useCallback((event) => {
    if (!isLive || isInteractiveScreen || contentItems.length <= 1) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - bounds.left;
    goToLocalLiveSlide(clickX < bounds.width / 2 ? -1 : 1);
  }, [contentItems.length, goToLocalLiveSlide, isInteractiveScreen, isLive]);

  useEffect(() => {
    if (!isLive || isInteractiveScreen || contentItems.length <= 1) {
      return undefined;
    }

    function handleLiveSlideKeyDown(event) {
      const target = event.target;
      const tagName = String(target?.tagName || "").toLowerCase();
      const isEditable =
        target?.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";

      if (isEditable || event.altKey || event.ctrlKey || event.metaKey) return;

      if (LIVE_NEXT_SLIDE_KEYS.has(event.key)) {
        event.preventDefault();
        goToLocalLiveSlide(1);
      } else if (LIVE_PREVIOUS_SLIDE_KEYS.has(event.key)) {
        event.preventDefault();
        goToLocalLiveSlide(-1);
      }
    }

    window.addEventListener("keydown", handleLiveSlideKeyDown);
    return () => window.removeEventListener("keydown", handleLiveSlideKeyDown);
  }, [contentItems.length, goToLocalLiveSlide, isInteractiveScreen, isLive]);

  useEffect(() => {
    if (!hasDisplayCode) {
      return undefined;
    }

    if (!shouldAutoAdvanceLoop) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % contentItems.length);
    }, activeDurationSeconds * 1000);

    return () => window.clearTimeout(timeout);
  }, [activeDurationSeconds, activeIndex, contentItems.length, hasDisplayCode, shouldAutoAdvanceLoop]);

  useEffect(() => {
    const intervalMs = isActiveLiveClockSlide ? 1000 : 30000;
    const interval = window.setInterval(() => {
      setScheduleTick(Date.now());
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [isActiveLiveClockSlide]);

  const wakeSleepScreen = useCallback(() => {
    const nextAwakeUntil = Date.now() + SLEEP_WAKE_IDLE_MS;
    setSleepAwakeUntil(nextAwakeUntil);
    setScheduleTick(Date.now());

    if (sleepWakeTimerRef.current) {
      window.clearTimeout(sleepWakeTimerRef.current);
    }

    sleepWakeTimerRef.current = window.setTimeout(() => {
      setScheduleTick(Date.now());
    }, SLEEP_WAKE_IDLE_MS + 75);
  }, []);

  useEffect(() => {
    return () => {
      if (sleepWakeTimerRef.current) {
        window.clearTimeout(sleepWakeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!sleepWindowActive && sleepAwakeUntil > 0) {
      setSleepAwakeUntil(0);
    }
  }, [sleepAwakeUntil, sleepWindowActive]);

  useEffect(() => {
    if (!sleepWindowActive || sleepAwakeUntil <= Date.now()) {
      return undefined;
    }

    const handleActivity = () => {
      wakeSleepScreen();
    };

    window.addEventListener("pointerdown", handleActivity, { passive: true });
    window.addEventListener("keydown", handleActivity);

    return () => {
      window.removeEventListener("pointerdown", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, [sleepAwakeUntil, sleepWindowActive, wakeSleepScreen]);

  useEffect(() => {
    if (!displayData?.display || !registeredScreen?.screenCode) {
      return undefined;
    }

    let mounted = true;

    async function refreshScreenStatus() {
      try {
        const nextScreen = await loadChurchLiveScreenStatus(
          displayData.display,
          registeredScreen.screenCode
        );

        if (!mounted) return;

        if (!nextScreen) {
          setRegisteredScreen(null);
          return;
        }

        setRegisteredScreen(nextScreen);
        setDisplayData((currentData) => {
          if (!currentData?.display) return currentData;

          const currentScreens = currentData.screens || [];
          const hasScreen = currentScreens.some(
            (screen) => screen.screenCode === nextScreen.screenCode
          );
          const screens = hasScreen
            ? currentScreens.map((screen) =>
                screen.screenCode === nextScreen.screenCode ? nextScreen : screen
              )
            : [...currentScreens, nextScreen];

          return {
            ...currentData,
            screens,
          };
        });
      } catch (screenStatusError) {
        console.error("Live display screen status refresh error:", screenStatusError);
      }
    }

    refreshScreenStatus();
    const interval = window.setInterval(refreshScreenStatus, SCREEN_STATUS_REFRESH_INTERVAL_MS);

    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [displayData?.display, registeredScreen?.screenCode]);

  useEffect(() => {
    if (!isLive) return;

    [activeIndex - 1, activeIndex, activeIndex + 1].forEach((slideIndex) => {
      const imageUrl = contentItems[slideIndex]?.imageUrl;
      if (!imageUrl) return;

      const image = new Image();
      image.decoding = "sync";
      image.src = imageUrl;
      image.decode?.().catch(() => null);
    });
  }, [activeIndex, contentItems, isLive]);

  useEffect(() => {
    previousSlideMetaRef.current = {
      isLiveHymn: isActiveLiveHymn,
      key: activeTransitionKey,
    };
  }, [activeTransitionKey, isActiveLiveHymn]);

  useEffect(() => {
    if (normalizedInteractiveAppId !== "members-directory") {
      setSelectedInteractiveFamilyId("");
    }
  }, [normalizedInteractiveAppId]);

  useEffect(() => {
    if (!isInteractiveScreen || (!interactiveMenuOpen && !normalizedInteractiveAppId)) {
      return undefined;
    }

    let idleTimeout = 0;

    const returnToSlides = () => {
      setInteractiveMenuOpen(false);
      setInteractiveFormNotice("");

      if (normalizedInteractiveAppId && displayCode) {
        navigate(`/live/${encodeURIComponent(displayCode)}`, { replace: true });
      }
    };

    const resetIdleTimer = () => {
      window.clearTimeout(idleTimeout);
      idleTimeout = window.setTimeout(returnToSlides, INTERACTIVE_RETURN_TO_SLIDES_IDLE_MS);
    };

    resetIdleTimer();
    window.addEventListener("pointerdown", resetIdleTimer, { passive: true });
    window.addEventListener("touchstart", resetIdleTimer, { passive: true });
    window.addEventListener("click", resetIdleTimer);
    window.addEventListener("keydown", resetIdleTimer);

    return () => {
      window.clearTimeout(idleTimeout);
      window.removeEventListener("pointerdown", resetIdleTimer);
      window.removeEventListener("touchstart", resetIdleTimer);
      window.removeEventListener("click", resetIdleTimer);
      window.removeEventListener("keydown", resetIdleTimer);
    };
  }, [
    displayCode,
    interactiveMenuOpen,
    isInteractiveScreen,
    navigate,
    normalizedInteractiveAppId,
  ]);

  function openInteractiveApp(appId) {
    if (!displayCode || !appId) return;
    setInteractiveMenuOpen(false);
    setInteractiveFormNotice("");
    setSelectedInteractiveFamilyId("");
    navigate(`/live/${encodeURIComponent(displayCode)}/interactive/${encodeURIComponent(appId)}`);
  }

  function returnToInteractiveMenu() {
    if (displayCode) {
      navigate(`/live/${encodeURIComponent(displayCode)}`);
    }
    setInteractiveMenuOpen(true);
    setInteractiveFormNotice("");
    setSelectedInteractiveFamilyId("");
  }

  function handleInteractiveFormSubmit(event, message) {
    event.preventDefault();
    event.currentTarget.reset();
    setInteractiveFormNotice(message);
  }

  async function handleMensDutySignupSubmit(event) {
    event.preventDefault();
    const accountId = displayData?.display?.accountId || "";

    if (!accountId) {
      setInteractiveFormNotice("This screen is still loading church settings. Please try again.");
      return;
    }

    try {
      await createPublicMensDuty(accountId, {
        ...mensDutySignup,
        showOnLive: true,
      });
      setInteractiveFormNotice("Thank you. Your duty signup has been saved.");
      setMensDutySignup((current) => ({
        dutyDate: current.dutyDate,
        dutyName: current.dutyName,
        assigneeName: "",
        note: "",
      }));
    } catch (signupError) {
      console.error("Interactive men's duty signup error:", signupError);
      setInteractiveFormNotice(signupError?.message || "Could not save this signup. Please try again.");
    }
  }

  function renderInteractiveMensDuties(loopItem) {
    const sundayOptions = getUpcomingSundayOptions(8);
    const dutyTypes = Array.isArray(displayData?.display?.mensDutyTypes)
      ? displayData.display.mensDutyTypes
      : [];
    const dutyTypeOptions = dutyTypes.length
      ? dutyTypes
      : [
          { id: "opening-prayer", name: "Opening Prayer" },
          { id: "song-leader", name: "Song Leader" },
          { id: "scripture-reading", name: "Scripture Reading" },
          { id: "table-talk", name: "Table Talk" },
          { id: "closing-prayer", name: "Closing Prayer" },
        ];
    const entries = Array.isArray(loopItem?.entries) ? loopItem.entries : [];

    return (
      <div style={styles.interactiveDutiesGrid}>
        <section style={styles.interactiveDutiesPanel}>
          <div style={styles.interactiveDutiesPanelTitle}>Upcoming Sunday</div>
          <div style={styles.interactiveDutiesPanelSubtitle}>
            {loopItem?.subtitle || "Current duty list"}
          </div>
          {entries.length > 0 ? (
            <div style={styles.interactiveDutiesList}>
              {entries.map((entry, index) => (
                <div key={entry.id || `interactive-duty-${index}`} style={styles.interactiveDutiesRow}>
                  <span style={styles.interactiveDutiesRole}>{entry.title || entry.label || "Duty"}</span>
                  <strong style={styles.interactiveDutiesName}>{entry.subtitle || "Open"}</strong>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.interactiveAppEmpty}>
              {loopItem?.body || "No duties are assigned yet. Use the form to sign up."}
            </div>
          )}
        </section>

        <form style={styles.interactiveForm} onSubmit={handleMensDutySignupSubmit}>
          <select
            style={styles.interactiveInput}
            value={mensDutySignup.dutyDate}
            onChange={(event) =>
              setMensDutySignup((current) => ({ ...current, dutyDate: event.target.value }))
            }
          >
            {sundayOptions.map((sunday) => (
              <option key={sunday.value} value={sunday.value}>
                {sunday.label}
              </option>
            ))}
          </select>
          <select
            style={styles.interactiveInput}
            value={mensDutySignup.dutyName || dutyTypeOptions[0]?.name || ""}
            onChange={(event) =>
              setMensDutySignup((current) => ({ ...current, dutyName: event.target.value }))
            }
          >
            {dutyTypeOptions.map((dutyType) => (
              <option key={dutyType.id || dutyType.name} value={dutyType.name}>
                {dutyType.name}
              </option>
            ))}
          </select>
          <input
            style={styles.interactiveInput}
            value={mensDutySignup.assigneeName}
            onChange={(event) =>
              setMensDutySignup((current) => ({ ...current, assigneeName: event.target.value }))
            }
            placeholder="Your name"
            required
          />
          <textarea
            style={styles.interactiveTextarea}
            value={mensDutySignup.note}
            onChange={(event) =>
              setMensDutySignup((current) => ({ ...current, note: event.target.value }))
            }
            placeholder="Optional note"
            rows={3}
          />
          <button type="submit" style={styles.interactiveSubmitButton}>Sign Up</button>
          {interactiveFormNotice ? <div style={styles.interactiveFormNotice}>{interactiveFormNotice}</div> : null}
        </form>
      </div>
    );
  }

  function renderInteractiveItemList(item, emptyText) {
    const entries = Array.isArray(item?.entries) ? item.entries : [];

    if (entries.length === 0) {
      return <div style={styles.interactiveAppEmpty}>{item?.body || emptyText}</div>;
    }

    return (
      <div style={styles.interactiveAppList}>
        {entries.slice(0, 18).map((entry, index) => (
          <div key={entry.id || `${item?.id || "entry"}-${index}`} style={styles.interactiveAppListItem}>
            {entry.imageUrl ? (
              <img src={entry.imageUrl} alt={entry.title || "Directory item"} style={styles.interactiveAppListImage} />
            ) : null}
            <div style={styles.interactiveAppListCopy}>
              <div style={styles.interactiveAppListTitle}>{entry.title || entry.label || item?.title}</div>
              {entry.body || entry.subtitle || entry.location ? (
                <div style={styles.interactiveAppListBody}>
                  {[entry.label, entry.body, entry.subtitle, entry.location].filter(Boolean).join(" • ")}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderInteractivePreacher(item) {
    const preacher = item?.preacher || {};
    const imageUrl = preacher.imageUrl || item?.imageUrl || item?.entries?.[0]?.imageUrl || "";
    const name = preacher.name || item?.title || "Our Preacher";
    const title = preacher.title || item?.subtitle || "Preacher";
    const bio = preacher.bio || item?.entries?.[0]?.body || item?.body || "";
    const email = preacher.email || "";
    const phone = preacher.phone || "";

    if (!item && !imageUrl && !bio && !email && !phone) {
      return <div style={styles.interactiveAppEmpty}>The preacher profile will appear here once it is added in organization settings.</div>;
    }

    return (
      <div style={styles.interactivePreacherCard}>
        {imageUrl ? (
          <img src={imageUrl} alt={name} style={styles.interactivePreacherImage} />
        ) : (
          <div style={styles.interactivePreacherImageFallback}>
            <User size={58} />
          </div>
        )}
        <div style={styles.interactivePreacherCopy}>
          <div style={styles.interactivePreacherName}>{name}</div>
          {title ? <div style={styles.interactivePreacherTitle}>{title}</div> : null}
          {bio ? <div style={styles.interactivePreacherBio}>{bio}</div> : null}
          {email || phone ? (
            <div style={styles.interactivePreacherContact}>
              {[email, phone].filter(Boolean).join(" • ")}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function renderDirectoryPhoto(entry, alt, style) {
    if (entry?.imageUrl) {
      return <img src={entry.imageUrl} alt={alt} style={style} />;
    }

    const iconSize = Math.max(28, Math.round((Number.parseFloat(style.height) || 74) * 0.45));

    return (
      <div style={{ ...style, ...styles.interactiveDirectoryPhotoFallback }} aria-hidden="true">
        <User size={iconSize} strokeWidth={2.2} />
      </div>
    );
  }

  function renderDirectoryLine(Icon, text) {
    if (!text) return null;

    return (
      <div style={styles.interactiveDirectoryLine}>
        <Icon size={18} strokeWidth={2.2} />
        <span>{text}</span>
      </div>
    );
  }

  function getEntryInitials(name = "") {
    const words = String(name || "").trim().split(/\s+/).filter(Boolean);
    return words.slice(0, 2).map((word) => word[0]?.toUpperCase()).join("") || "?";
  }

  function isChildMemberRole(role = "") {
    return ["child", "children", "kid", "kids"].includes(String(role || "").trim().toLowerCase());
  }

  function getMemberAge(birthdate = "") {
    if (!birthdate) return "";

    const bornAt = new Date(`${birthdate}T00:00:00`);

    if (Number.isNaN(bornAt.getTime())) {
      return "";
    }

    const today = new Date();
    let age = today.getFullYear() - bornAt.getFullYear();
    const birthdayHasPassed =
      today.getMonth() > bornAt.getMonth() ||
      (today.getMonth() === bornAt.getMonth() && today.getDate() >= bornAt.getDate());

    if (!birthdayHasPassed) {
      age -= 1;
    }

    return age >= 0 ? String(age) : "";
  }

  function formatDirectoryDate(value = "") {
    if (!value) return "";

    const date = new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  function getYearsSinceDate(value = "") {
    if (!value) return "";

    const date = new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const today = new Date();
    let years = today.getFullYear() - date.getFullYear();
    const dateHasPassed =
      today.getMonth() > date.getMonth() ||
      (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());

    if (!dateHasPassed) {
      years -= 1;
    }

    return years >= 0 ? String(years) : "";
  }

  function getMemberRoleLabel(member) {
    const role = member?.role || "";
    const age = isChildMemberRole(role) ? getMemberAge(member?.birthdate) : "";

    if (role && age) {
      return `${role} • Age ${age}`;
    }

    return role;
  }

  function getFamilyAnniversaryValue(family, members = []) {
    const memberWithFamilyAnniversary = members.find((member) => (
      member?.familyAnniversary ||
      member?.family_anniversary ||
      member?.family?.anniversary ||
      member?.family?.anniversary_date
    ));

    return (
      family?.anniversary ||
      family?.familyAnniversary ||
      family?.family_anniversary ||
      family?.anniversaryDate ||
      family?.anniversary_date ||
      memberWithFamilyAnniversary?.familyAnniversary ||
      memberWithFamilyAnniversary?.family_anniversary ||
      memberWithFamilyAnniversary?.family?.anniversary ||
      memberWithFamilyAnniversary?.family?.anniversary_date ||
      ""
    );
  }

  function getFamilyAnniversaryLabel(family, members = []) {
    const anniversaryValue = getFamilyAnniversaryValue(family, members);
    const anniversary = formatDirectoryDate(anniversaryValue);

    if (!anniversary) return "";

    const yearsMarried = getYearsSinceDate(anniversaryValue);
    const yearsLabel = yearsMarried
      ? `${yearsMarried} ${yearsMarried === "1" ? "year" : "years"} married`
      : "";

    return ["Anniversary", anniversary, yearsLabel].filter(Boolean).join(" • ");
  }

  function getFamilyMembersForSlide(item, family) {
    const familyMembers = Array.isArray(family?.members)
      ? family.members
      : Array.isArray(family?.memberEntries)
        ? family.memberEntries
        : Array.isArray(family?.member_entries)
          ? family.member_entries
          : [];
    const members = Array.isArray(item?.memberEntries)
      ? item.memberEntries
      : Array.isArray(item?.member_entries)
        ? item.member_entries
        : Array.isArray(item?.members)
          ? item.members
          : [];

    if (familyMembers.length > 0) {
      return familyMembers;
    }

    return members.filter((member) => (
      (family?.id && (member.familyId === family.id || member.family_id === family.id)) ||
      (family?.title && (member.familyName === family.title || member.family_name === family.title)) ||
      (family?.familyName && (member.familyName === family.familyName || member.family_name === family.familyName))
    ));
  }

  function getFamilyTitle(family) {
    return family?.title || family?.familyName || family?.family_name || "Church family";
  }

  function getFamilyAddress(family) {
    return family?.address || family?.body || "";
  }

  function getFamilyId(family, index = 0) {
    return family?.id || `${getFamilyTitle(family)}-${index}`;
  }

  function getFamilyMirageTileStyle(index) {
    const layouts = [
      { left: "1%", top: "9%", width: "25%", height: "39%", transform: "rotate(-4deg)", zIndex: 2 },
      { left: "29%", top: "2%", width: "20%", height: "32%", transform: "rotate(2.5deg)", zIndex: 3 },
      { left: "52%", top: "8%", width: "22%", height: "36%", transform: "rotate(-1.5deg)", zIndex: 2 },
      { left: "76%", top: "1%", width: "21%", height: "34%", transform: "rotate(4deg)", zIndex: 3 },
      { left: "8%", top: "54%", width: "20%", height: "32%", transform: "rotate(3.5deg)", zIndex: 4 },
      { left: "32%", top: "40%", width: "25%", height: "42%", transform: "rotate(-2.5deg)", zIndex: 5 },
      { left: "61%", top: "51%", width: "19%", height: "31%", transform: "rotate(2deg)", zIndex: 4 },
      { left: "81%", top: "42%", width: "17%", height: "30%", transform: "rotate(-4.5deg)", zIndex: 2 },
      { left: "18%", top: "28%", width: "17%", height: "29%", transform: "rotate(1.5deg)", zIndex: 1 },
      { left: "47%", top: "69%", width: "18%", height: "27%", transform: "rotate(4.5deg)", zIndex: 3 },
    ];

    return layouts[index % layouts.length];
  }

  function renderFamilyMirage(item, limit = 10) {
    const families = Array.isArray(item?.entries) ? item.entries : [];

    return (
      <div style={styles.familyMirage}>
        {families.slice(0, limit).map((entry, index) => (
          <div
            key={entry.id || `${item.id}-${index}`}
            style={{ ...styles.familyMiragePhoto, ...getFamilyMirageTileStyle(index) }}
          >
            <img src={entry.imageUrl} alt={entry.title || "Church family"} style={styles.familyMirageImage} />
            <div style={styles.familyMirageInfo}>
              {entry.title ? <div style={styles.familyMirageCaption}>{entry.title}</div> : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderInteractiveMembersDirectory(item, emptyText) {
    const families = Array.isArray(item?.entries) ? item.entries : [];
    const members = Array.isArray(item?.memberEntries)
      ? item.memberEntries
      : Array.isArray(item?.members)
        ? item.members
        : [];
    if (families.length === 0 && members.length === 0) {
      return <div style={styles.interactiveAppEmpty}>{item?.body || emptyText}</div>;
    }

    const selectedFamily = families.find((family, index) => getFamilyId(family, index) === selectedInteractiveFamilyId);

    if (selectedFamily) {
      const familyMembers = getFamilyMembersForSlide(item, selectedFamily);
      const familyTitle = getFamilyTitle(selectedFamily);
      const familyAddress = getFamilyAddress(selectedFamily);
      const familyAnniversaryLabel = getFamilyAnniversaryLabel(selectedFamily, familyMembers);

      return (
        <div style={styles.interactiveFamilyDetail}>
          <section style={styles.interactiveFamilyHero}>
            {renderDirectoryPhoto(selectedFamily, familyTitle, styles.interactiveFamilyHeroPhoto)}
            <div style={styles.interactiveFamilyHeroOverlay}>
              <button
                type="button"
                style={styles.interactiveFamilyInlineBack}
                onClick={() => setSelectedInteractiveFamilyId("")}
              >
                <ChevronLeft size={20} strokeWidth={2.7} />
                <span>Families</span>
              </button>
              <div style={styles.interactiveFamilyHeroCopy}>
                <h2 style={styles.interactiveFamilyHeroTitle}>{familyTitle}</h2>
                {familyAddress ? (
                  <div style={styles.interactiveFamilyHeroAddress}>
                    <MapPin size={22} strokeWidth={2.5} />
                    <span>{familyAddress}</span>
                  </div>
                ) : null}
                {familyAnniversaryLabel ? (
                  <div style={styles.interactiveFamilyHeroAnniversary}>
                    <HeartHandshake size={22} strokeWidth={2.5} />
                    <span>{familyAnniversaryLabel}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <section style={styles.interactiveFamilyMemberSection}>
            <div style={styles.interactiveDirectorySectionHeader}>
              <span>Family Members</span>
              <strong>{familyMembers.length}</strong>
            </div>
            {familyMembers.length > 0 ? (
              <div style={styles.interactiveFamilyLargeMemberGrid}>
                {familyMembers.map((member, memberIndex) => {
                  const memberRoleLabel = getMemberRoleLabel(member);
                  const memberName = member.title || member.name || "Member";

                  return (
                    <article
                      key={member.id || `${selectedFamily.id || "family"}-detail-member-${memberIndex}`}
                      style={styles.interactiveFamilyLargeMemberCard}
                    >
                      {member.imageUrl ? (
                        <img
                          src={member.imageUrl}
                          alt={memberName}
                          style={styles.interactiveFamilyLargeMemberImage}
                        />
                      ) : (
                        <div style={styles.interactiveFamilyLargeMemberInitials}>
                          {getEntryInitials(memberName)}
                        </div>
                      )}
                      <div style={styles.interactiveFamilyLargeMemberCopy}>
                        <div style={styles.interactiveFamilyLargeMemberName}>{memberName}</div>
                        {memberRoleLabel ? (
                          <div style={styles.interactiveFamilyLargeMemberRole}>{memberRoleLabel}</div>
                        ) : null}
                        {member.birthdate ? (
                          <div style={styles.interactiveFamilyLargeMemberMetaLine}>
                            <CalendarDays size={18} strokeWidth={2.4} />
                            <span>Birthday {formatDirectoryDate(member.birthdate)}</span>
                          </div>
                        ) : null}
                        {[member.phone, member.email].filter(Boolean).length ? (
                          <div style={styles.interactiveFamilyLargeMemberMeta}>
                            {[member.phone, member.email].filter(Boolean).join(" • ")}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div style={styles.interactiveDirectoryMuted}>No active members listed yet.</div>
            )}
          </section>
        </div>
      );
    }

    return (
      <div style={styles.interactiveDirectory}>
        <section style={styles.interactiveDirectorySection}>
          <div style={styles.interactiveDirectorySectionHeader}>
            <span>Families</span>
            <strong>{families.length}</strong>
          </div>
          <div style={styles.interactiveDirectoryFamilyGrid}>
            {families.map((family, index) => {
              const familyMembers = getFamilyMembersForSlide(item, family);
              const familyTitle = getFamilyTitle(family);
              const familyAddress = getFamilyAddress(family);
              const familyId = getFamilyId(family, index);

              return (
                <button
                  key={family.id || `family-${index}`}
                  type="button"
                  style={styles.interactiveDirectoryFamilyCard}
                  onClick={() => setSelectedInteractiveFamilyId(familyId)}
                >
                  <div style={styles.interactiveDirectoryFamilyPhotoPanel}>
                    {renderDirectoryPhoto(
                      family,
                      familyTitle,
                      styles.interactiveDirectoryFamilyPhoto
                    )}
                    <div style={styles.interactiveDirectoryFamilyOverlay}>
                      <div style={styles.interactiveDirectoryOverlayName}>
                        {familyTitle}
                      </div>
                      {familyAddress ? (
                        <div style={styles.interactiveDirectoryOverlayAddress}>
                          <MapPin size={16} strokeWidth={2.3} />
                          <span>{familyAddress}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div style={styles.interactiveDirectoryFamilyRoster}>
                    <div style={styles.interactiveDirectoryRosterHeader}>
                      <span>Members</span>
                      <strong>{familyMembers.length}</strong>
                    </div>
                    {renderDirectoryLine(Phone, family.phone)}
                    {familyMembers.length > 0 ? (
                      <div style={styles.interactiveDirectoryFamilyMembers}>
                        {familyMembers.map((member, memberIndex) => {
                          const memberRoleLabel = getMemberRoleLabel(member);

                          return (
                            <div
                              key={member.id || `${family.id || "family"}-member-${memberIndex}`}
                              style={styles.interactiveDirectoryFamilyMember}
                            >
                              {member.imageUrl ? (
                                <img
                                  src={member.imageUrl}
                                  alt={member.title || "Family member"}
                                  style={styles.interactiveDirectoryFamilyMemberImage}
                                />
                              ) : (
                                <div style={styles.interactiveDirectoryFamilyMemberInitials}>
                                  {getEntryInitials(member.title || member.name)}
                                </div>
                              )}
                              <span style={styles.interactiveDirectoryFamilyMemberText}>
                                <span style={styles.interactiveDirectoryFamilyMemberName}>
                                  {member.title || member.name || "Member"}
                                </span>
                                {memberRoleLabel ? (
                                  <span style={styles.interactiveDirectoryFamilyMemberRole}>{memberRoleLabel}</span>
                                ) : null}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={styles.interactiveDirectoryMuted}>No active members listed yet.</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  function renderInteractiveAppPage() {
    const app = selectedInteractiveAppEnabled ? selectedInteractiveApp : INTERACTIVE_APP_DEFINITIONS[0];
    const isPhonePage = app.id === "phone-page";
    const loopItem = getInteractiveLoopItem(displayData.display, app.id);
    const cachedChurchBranding = getCachedChurchOrganizationBranding();
    const logoUrl = displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl || "";
    const churchName = displayData.display.organizationName || cachedChurchBranding.name || "Church";
    const isFamilyDetailPage = app.id === "members-directory" && Boolean(selectedInteractiveFamilyId);
    const interactiveBackgroundUrl = globalStyles?.interactiveBackgroundUrl || "";

    return (
      <div
        style={{
          ...styles.interactiveAppPage,
          ...(interactiveBackgroundUrl
            ? {
                backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.84), rgba(255,255,255,0.92)), url(${interactiveBackgroundUrl})`,
              }
            : {}),
          ...(isPhonePage ? styles.interactiveAppPageCompact : {}),
        }}
      >
        <div style={{ ...styles.interactiveAppHeader, ...(isPhonePage ? styles.interactiveAppHeaderCompact : {}) }}>
          <div style={styles.interactiveAppHeading}>
            <div style={{ ...styles.interactiveAppTitleLarge, ...(isPhonePage ? styles.interactiveAppTitleCompact : {}) }}>
              {app.title}
            </div>
            <div style={{ ...styles.interactiveAppSubtitle, ...(isPhonePage ? styles.interactiveAppSubtitleCompact : {}) }}>
              {app.subtitle}
            </div>
          </div>
          <div style={styles.interactiveAppHeaderActions}>
            {logoUrl ? <img src={logoUrl} alt={churchName} style={styles.interactiveAppLogo} /> : null}
          </div>
        </div>

        <div style={{ ...styles.interactiveAppBody, ...(isPhonePage ? styles.interactiveAppBodyCompact : {}) }}>
          {app.id === "members-directory" ? (
            renderInteractiveMembersDirectory(loopItem, "The public member directory will appear here once families and members are available.")
          ) : app.id === "announcements" ? (
            renderInteractiveItemList(loopItem, "Announcements will appear here.")
          ) : app.id === "events" ? (
            renderInteractiveItemList(loopItem, "Events will appear here.")
          ) : app.id === "surrounding-events" ? (
            renderInteractiveItemList(loopItem, "Shared events from other Oikos Church organizations will appear here.")
          ) : app.id === "mens-duties" ? (
            renderInteractiveMensDuties(loopItem)
          ) : app.id === "preacher" ? (
            renderInteractivePreacher(loopItem)
          ) : app.id === "prayer-request" ? (
            <form
              style={styles.interactiveForm}
              onSubmit={(event) => handleInteractiveFormSubmit(event, "Thank you. Your prayer request has been received on this screen.")}
            >
              <input style={styles.interactiveInput} name="name" placeholder="Your name" />
              <textarea style={styles.interactiveTextarea} name="request" placeholder="Prayer request" rows={5} />
              <button type="submit" style={styles.interactiveSubmitButton}>Submit Prayer Request</button>
              {interactiveFormNotice ? <div style={styles.interactiveFormNotice}>{interactiveFormNotice}</div> : null}
            </form>
          ) : app.id === "visitor-log" ? (
            <form
              style={styles.interactiveForm}
              onSubmit={(event) => handleInteractiveFormSubmit(event, "Thank you for visiting. We are glad you are here.")}
            >
              <input style={styles.interactiveInput} name="name" placeholder="Name" />
              <input style={styles.interactiveInput} name="phone" placeholder="Phone or email" />
              <textarea style={styles.interactiveTextarea} name="note" placeholder="Anything you would like us to know?" rows={4} />
              <button type="submit" style={styles.interactiveSubmitButton}>Check In</button>
              {interactiveFormNotice ? <div style={styles.interactiveFormNotice}>{interactiveFormNotice}</div> : null}
            </form>
          ) : app.id === "phone-page" ? (
            <div style={styles.phoneQrGrid}>
              <div style={styles.phoneQrCard}>
                <img src={churchAppQrUrl} alt="QR code for church sign in" style={styles.phoneQrImage} />
                <div style={styles.phoneQrTitle}>Get Connected</div>
                <div style={styles.phoneQrBody}>
                  Scan to open the church sign in page and request a login.
                </div>
                <div style={styles.phoneQrLink}>{churchAppUrl.replace(/^https?:\/\//, "")}</div>
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ ...styles.interactiveAppFooter, ...(isPhonePage ? styles.interactiveAppFooterCompact : {}) }}>
          <button
            type="button"
            style={styles.interactiveBottomBackButton}
            onClick={isFamilyDetailPage ? () => setSelectedInteractiveFamilyId("") : returnToInteractiveMenu}
          >
            <ChevronLeft size={22} strokeWidth={2.7} />
            <span>{isFamilyDetailPage ? "Back to Families" : "Back to Menu"}</span>
          </button>
        </div>
      </div>
    );
  }

  function renderInteractiveMenu() {
    const cachedChurchBranding = getCachedChurchOrganizationBranding();
    const logoUrl = displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl || ChurchLogo;
    const interactiveBackgroundUrl = globalStyles?.interactiveBackgroundUrl || "";

    return (
      <div
        style={{
          ...styles.interactiveMenuPage,
          ...(interactiveBackgroundUrl
            ? {
                backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.76), rgba(246,249,247,0.84)), url(${interactiveBackgroundUrl})`,
              }
            : {}),
        }}
      >
        <style>{INTERACTIVE_MENU_MOTION_CSS}</style>
        <img src={ChurchIcon} alt="Oikos Church" style={styles.interactiveMenuCornerIcon} />
        <section style={styles.interactiveMenuHero}>
          <img
            className="interactive-church-logo"
            src={logoUrl}
            alt="Church logo"
            style={styles.interactiveMenuLogo}
          />
        </section>
        <div className="interactive-church-menu-grid" style={styles.interactiveMenuGrid}>
          {interactiveApps.map((app, index) => {
            const appImage = app.image || ChurchIcon;

            return (
              <button
                className="interactive-church-app-button"
                key={app.id}
                type="button"
                style={{
                  ...styles.interactiveMenuTile,
                  ...getInteractiveAppEntryMotion(index),
                }}
                onClick={() => openInteractiveApp(app.id)}
              >
                <span style={styles.interactiveMenuIconFrame}>
                  <img src={appImage} alt="" style={styles.interactiveMenuIconImage} />
                </span>
                <span style={styles.interactiveMenuTileCopy}>
                  <span style={styles.interactiveMenuTileTitle}>{app.menuLabel}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function renderInteractiveSlide() {
    const item = contentItems[activeIndex] || contentItems[0] || interactiveSlideItems[0];
    const cachedChurchBranding = getCachedChurchOrganizationBranding();
    const logoUrl = displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl || "";
    const isMembersItem = item?.appId === "members-directory";

    return (
      <div
        style={styles.page}
        onClick={() => setInteractiveMenuOpen(true)}
        onPointerDown={() => setInteractiveMenuOpen(true)}
      >
        <style>{INTERACTIVE_TOUCH_PROMPT_CSS}</style>
        <img src={ChurchIcon} alt="Oikos Church" style={styles.interactiveSlideCornerIcon} />
        {logoUrl ? (
          <img src={logoUrl} alt="Church logo" style={styles.interactiveSlideOrgLogo} />
        ) : null}
        <div style={styles.viewport}>
          <div style={{ ...styles.slideSurface, ...styles.interactiveSlideSurface }}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={item?.id || "interactive-slide"}
                initial={{ opacity: 0, scale: 0.998 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.002 }}
                transition={{ duration: 0.36, ease: "easeOut" }}
                style={styles.slideContent}
              >
                <div style={{ ...styles.loopSlide, ...styles.loopInfoSlide, ...styles.interactiveLoopSlide }}>
                  <div style={styles.loopInfoTitle}>{item?.title || "Welcome"}</div>
                  {item?.subtitle ? <div style={styles.loopInfoSubtitle}>{item.subtitle}</div> : null}
                  {isMembersItem && Array.isArray(item?.entries) && item.entries.length > 0 ? (
                    renderFamilyMirage(item, 10)
                  ) : (
                    renderLoopEntries(item) || <div style={styles.loopInfoBody}>{item?.body || ""}</div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="interactive-touch-prompt" style={styles.interactiveTouchTag}>
              <span style={styles.interactiveTouchDot} aria-hidden="true" />
              <span>Touch to open menu</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasDisplayCode) {
    return (
      <div style={styles.codeEntryPage}>
        <form style={styles.codeEntryCard} onSubmit={handleScreenCodeSubmit}>
          <img src={ChurchLogo} alt="Oikos Church" style={styles.codeEntryLogo} />
          <div style={styles.codeEntryEyebrow}>Live Display</div>
          <h1 style={styles.codeEntryTitle}>Connect This Screen</h1>
          <div style={styles.codeEntryText}>
            Enter the live display code shown in the Church Live Display controls.
          </div>
          <input
            autoComplete="off"
            autoFocus
            inputMode="text"
            onChange={(event) => setScreenCode(event.target.value.toUpperCase())}
            placeholder="Screen code"
            style={styles.codeEntryInput}
            value={screenCode}
          />
          <button style={styles.codeEntryButton} type="submit">
            Connect
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return <LiveDisplayPreparingScreen />;
  }

  if (error || !displayData?.display) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyCard}>
          <div style={styles.emptyTitle}>Live Display Unavailable</div>
          <div style={styles.emptyText}>{error || "No live display found for this link."}</div>
        </div>
      </div>
    );
  }

  if (isIdentifyMode) {
    return (
      <div style={styles.identifyPage}>
        <style>
          {`
            @keyframes oikosIdentifyCorePulse {
              0%, 100% {
                box-shadow:
                  0 0 34px rgba(255,255,255,0.42),
                  0 0 86px rgba(var(--color-primary-rgb),0.48);
                transform: scale(1);
              }
              50% {
                box-shadow:
                  0 0 58px rgba(255,255,255,0.76),
                  0 0 148px rgba(var(--color-primary-rgb),0.78);
                transform: scale(1.035);
              }
            }

            @keyframes oikosIdentifyRingPulse {
              0% {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.08);
              }
              14% {
                opacity: 0.72;
                transform: translate(-50%, -50%) scale(0.16);
              }
              58% {
                opacity: 0.28;
                transform: translate(-50%, -50%) scale(0.76);
              }
              100% {
                opacity: 0;
                transform: translate(-50%, -50%) scale(1.34);
              }
            }

            @keyframes oikosIdentifyBeaconGlow {
              0%, 100% {
                opacity: 0.58;
                transform: translateX(-50%) scaleX(0.82);
              }
              50% {
                opacity: 1;
                transform: translateX(-50%) scaleX(1.08);
              }
            }
          `}
        </style>
        <div style={styles.identifyBeaconWrap}>
          <div style={{ ...styles.identifyBeaconRing, ...styles.identifyBeaconRingInner }} />
          <div style={{ ...styles.identifyBeaconRing, ...styles.identifyBeaconRingOuter }} />
          <div style={styles.identifyBeaconGlow} />
          <div style={styles.identifyBeaconCore}>
            <img src={ChurchIcon} alt="Oikos Church" style={styles.identifyIcon} />
          </div>
        </div>
        <div style={styles.identifyKicker}>Oikos Church Display</div>
        <div style={styles.identifyName}>{currentScreen?.name || "Live screen"}</div>
        <div style={styles.identifyCode}>{currentScreen?.screenCode || ""}</div>
      </div>
    );
  }

  if (shouldShowSleepScreen) {
    return (
      <div onClick={wakeSleepScreen} onPointerDown={wakeSleepScreen} style={styles.sleepPage}>
        <style>
          {`
            @keyframes oikosSleepBreath {
              0%, 100% {
                opacity: 0.18;
                transform: scale(0.96);
              }
              50% {
                opacity: 0.3;
                transform: scale(1.04);
              }
            }

            @keyframes oikosSleepZFloat {
              0% {
                opacity: 0;
                transform: translate3d(0, 12px, 0) scale(0.8) rotate(-6deg);
              }
              18% {
                opacity: 0.38;
              }
              82% {
                opacity: 0.24;
              }
              100% {
                opacity: 0;
                transform: translate3d(34px, -104px, 0) scale(1.35) rotate(9deg);
              }
            }
          `}
        </style>
        <div style={styles.sleepIconWrap}>
          <img src={ChurchIcon} alt="Oikos Church" style={styles.sleepIcon} />
          <div style={{ ...styles.sleepZzz, ...styles.sleepZzzOne }}>Z</div>
          <div style={{ ...styles.sleepZzz, ...styles.sleepZzzTwo }}>Z</div>
          <div style={{ ...styles.sleepZzz, ...styles.sleepZzzThree }}>Z</div>
        </div>
      </div>
    );
  }

  if (normalizedInteractiveAppId) {
    return renderInteractiveAppPage();
  }

  if (isInteractiveScreen) {
    return interactiveMenuOpen ? renderInteractiveMenu() : renderInteractiveSlide();
  }

  const hasNoActiveLoopSlides = !isLive && contentItems.length === 0;
  const item = hasNoActiveLoopSlides
    ? {
        id: "no-active-loop-slides",
        title: "No Pre-Service Slides Active",
        subtitle: "",
        body: "Turn on at least one pre-service loop slide in Live Display to show content here.",
        tone: "empty",
        type: "status",
      }
    : activeContentItem;
  const sourceLabel = getContentSourceLabel(item, isLive);
  const isLiveHymn = isLive && item?.itemType === "hymn";
  const isLiveImageSlide =
    isLive &&
    Boolean(item?.imageUrl) &&
    (item?.itemType === "hymn" || item?.itemType === "visual_slide");
  const isLiveHymnTitlePage =
    isLive &&
    item?.itemType === "custom_slide" &&
    String(item?.id || "").includes("hymn-title-page");
  const isLiveInvitationSongPage =
    isLiveHymnTitlePage &&
    ["invitation song", "invitation song announcement"].includes(
      String(item?.title || "").trim().toLowerCase()
    );
  const invitationSongParts = isLiveInvitationSongPage
    ? getInvitationSongParts(item?.body || item?.subtitle || "")
    : null;
  const isLiveClockSlide = isLive && isServiceClockSlide(item);
  const isLiveStartServiceSlide = isLive && item?.sourceType === "start-service-slide";
  const isLiveThankYouSlide =
    isLive && (item?.sourceType === "end-service-slide" || item?.sourceType === "thank-you-slide");
  const isWelcomeSlide = !isLive && (item?.id === "welcome-slide" || item?.type === "welcome");
  const isLogoSlide = !isLive && (item?.id === "logo-slide" || item?.type === "logo");
  const isMembersSlide = !isLive && (item?.id === "members-families-slide" || item?.type === "members");
  const isEventsSlide = !isLive && item?.id === "events-slide";
  const isCurrentHymnsSlide = !isLive && (item?.id === "current-hymns-slide" || item?.type === "current-hymns");
  const isPreacherSlide = !isLive && (item?.id === "preacher-profile-slide" || item?.type === "preacher");
  const loopKickerLabel =
    !isLive && !isWelcomeSlide && item?.tone
      ? String(item.tone).trim()
      : "";
  const shouldShowLoopSubtitle =
    item?.subtitle &&
    !["announcements-slide", "events-slide"].includes(item?.id);
  const cachedChurchBranding = getCachedChurchOrganizationBranding();
  const savedLoopLogoUrl =
    contentItems.find((loopItem) => loopItem?.logoUrl)?.logoUrl || "";
  const loopLogoUrl = !isLive
    ? item?.logoUrl || displayData.display.organizationLogoUrl || savedLoopLogoUrl || cachedChurchBranding.logoUrl || ""
    : "";
  const liveServiceLogoUrl = displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl || "";
  const liveServiceOrganizationName = displayData.display.organizationName || cachedChurchBranding.name || "Church";
  const sundayMorningServiceTime = getSundayMorningServiceTime(displayData.display.serviceTimes);
  const hymnSlideVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0, transition: { duration: 0 } },
  };
  const slideMotion = isLiveHymn
    ? {
        variants: hymnSlideVariants,
        transition: { duration: 0.18, ease: "easeOut" },
      }
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0, transition: { duration: 0 } },
        transition: { duration: isLive ? 0.18 : 0.36, ease: "easeOut" },
      };

  return (
    <div
      style={styles.page}
      onPointerDownCapture={handleLiveSlidePointerDown}
    >
      {!isLive && loopLogoUrl ? (
        <img
          src={loopLogoUrl}
          alt="Church logo"
          style={{ ...styles.loopHeaderLogo, ...styles.preServiceHeaderLogo }}
        />
      ) : null}
      <div style={styles.viewport}>
        <div
          style={{
            ...styles.slideSurface,
            ...(isLive && !isInteractiveScreen && contentItems.length > 1 ? styles.liveClickableSlideSurface : {}),
            ...(isLive && item?.itemType === "title_slide" ? styles.titleSlideSurface : {}),
            ...(isLive && item?.itemType === "verse" ? styles.liveVerseSlideSurface : {}),
            ...(isLiveImageSlide ? styles.liveHymnSlideSurface : {}),
          }}
        >
          <AnimatePresence
            custom={isHymnToHymnTransition}
            mode="wait"
            initial={false}
          >
            <motion.div
              key={activeTransitionKey}
              custom={isHymnToHymnTransition}
              variants={slideMotion.variants}
              initial={slideMotion.variants ? "initial" : slideMotion.initial}
              animate={slideMotion.variants ? "animate" : slideMotion.animate}
              exit={slideMotion.variants ? "exit" : slideMotion.exit}
              transition={slideMotion.transition}
              style={{
                ...styles.slideContent,
                ...(isLiveImageSlide ? styles.liveHymnSlideContent : {}),
              }}
            >
              {isLive ? (
                item?.itemType === "visual_slide" && item?.imageUrl ? (
                  <div style={styles.imageSlide}>
                    <img
                      src={item.imageUrl}
                      alt={item.title || "Visual slide"}
                      decoding="sync"
                      fetchPriority="high"
                      loading="eager"
                      style={{
                        ...styles.imageSlideAsset,
                        objectFit: item.imageFit === "cover" ? "cover" : "contain",
                        objectPosition: `${
                          Number.isFinite(Number(item.imagePositionX)) ? Number(item.imagePositionX) : 50
                        }% ${
                          Number.isFinite(Number(item.imagePositionY)) ? Number(item.imagePositionY) : 50
                        }%`,
                      }}
                    />
                  </div>
                ) : item?.itemType === "title_slide" ? (
                  <div style={styles.titleSlide}>
                    <div style={{ ...styles.titleSlideTitle, ...getOneLineTitleStyle(item.title) }}>
                      {item.title || "Sermon"}
                    </div>
                    {item.body || item.subtitle || item.date ? (
                      <div style={styles.titleSlideMeta}>
                        {[item.subtitle, item.date].filter(Boolean).join(" • ") || item.body}
                      </div>
                    ) : null}
                  </div>
                ) : item?.itemType === "verse" ? (
                  <div style={styles.liveVerseSlide}>
                    <div style={{ ...styles.liveVerseText, ...getLiveVerseTextStyle(item) }}>
                      {getLiveVerseLines(item).map((line, lineIndex) => (
                        <div key={`${item.id || "verse"}-${lineIndex}`} style={styles.liveVerseLine}>
                          <div style={styles.liveVerseBody}>
                            {line.number ? (
                              <span style={styles.liveVerseNumber}>{line.number}</span>
                            ) : null}
                            {getVerseTextSegments(line.text).map((segment, segmentIndex) =>
                              segment.type === "number" ? (
                                <span
                                  key={`${item.id || "verse"}-${lineIndex}-${segmentIndex}`}
                                  style={styles.liveVerseNumber}
                                >
                                  {segment.value}
                                </span>
                              ) : (
                                <span key={`${item.id || "verse"}-${lineIndex}-${segmentIndex}`}>
                                  {segment.value}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : item?.itemType === "hymn" ? (
                  item?.imageUrl ? (
                    <div style={styles.imageSlide}>
                      <img
                        src={item.imageUrl}
                        alt={item.title || "Hymn slide"}
                        decoding="sync"
                        fetchPriority="high"
                        loading="eager"
                        style={styles.imageSlideAsset}
                      />
                      {item?.isEndOfSong ? (
                        <div style={styles.endOfSongBadge}>End Of Song</div>
                      ) : null}
                    </div>
                  ) : (
                    <div style={styles.verseSlide}>
                      <div style={styles.hymnTextSlideTitle}>
                        {item.title}
                      </div>
                      <div style={styles.hymnTextSlideBody}>{item.body}</div>
                      {item?.isEndOfSong ? (
                        <div style={styles.endOfSongText}>End Of Song</div>
                      ) : null}
                    </div>
                  )
                ) : (
                  <div
                    style={{
                      ...styles.loopSlide,
                      ...styles.liveServiceHeaderSlide,
                      ...(isLiveHymnTitlePage ? styles.liveServiceSongSlide : {}),
                      ...(isLiveStartServiceSlide || isLiveThankYouSlide ? styles.liveServiceLogoSlide : {}),
                    }}
                  >
                    {isLiveClockSlide ? (
                      <>
                        <div style={styles.liveServiceClockTime}>{formatLiveClockTime(scheduleTick)}</div>
                        {sundayMorningServiceTime ? (
                          <div style={styles.liveServiceClockLabel}>{sundayMorningServiceTime}</div>
                        ) : null}
                      </>
                    ) : isLiveStartServiceSlide ? (
                      <>
                        {liveServiceLogoUrl ? (
                          <img
                            src={liveServiceLogoUrl}
                            alt={liveServiceOrganizationName}
                            style={styles.liveServiceFeatureLogo}
                          />
                        ) : null}
                        <div style={styles.liveServiceLogoTitle}>Welcome to</div>
                        <div style={styles.liveServiceOrganizationName}>{liveServiceOrganizationName}</div>
                      </>
                    ) : isLiveThankYouSlide ? (
                      <>
                        {liveServiceLogoUrl ? (
                          <img
                            src={liveServiceLogoUrl}
                            alt={liveServiceOrganizationName}
                            style={styles.liveServiceFeatureLogo}
                          />
                        ) : null}
                        <div style={styles.liveServiceThankYouTitle}>Thank you for joining us</div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            ...styles.loopTitle,
                            ...(isLiveHymnTitlePage ? styles.liveServiceSongTitle : {}),
                            ...(isLiveHymnTitlePage ? getLiveServiceSongTitleStyle(item?.title) : {}),
                          }}
                        >
                          {item?.title || "Live Service"}
                        </div>
                        {isLiveInvitationSongPage && invitationSongParts ? (
                          <div style={styles.liveServiceInvitationSongDetails}>
                            <div style={styles.liveServiceInvitationSongNumber}>
                              {invitationSongParts.number}
                            </div>
                            {invitationSongParts.name ? (
                              <div style={styles.liveServiceInvitationSongName}>
                                {invitationSongParts.name}
                              </div>
                            ) : null}
                          </div>
                        ) : item?.bodyHtml && item?.sourceType === "sermon-text" ? (
                          <div
                            style={styles.body}
                            dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                          />
                        ) : item?.body || item?.subtitle ? (
                          <div
                            style={{
                              ...styles.body,
                              ...(isLiveHymnTitlePage ? styles.liveServiceSongNumber : {}),
                              ...(isLiveHymnTitlePage ? getLiveServiceSongNumberStyle(item?.title) : {}),
                            }}
                          >
                            {item?.body || item?.subtitle}
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                )
              ) : isLogoSlide ? (
                <div style={{ ...styles.loopSlide, ...styles.logoLoopSlide }}>
                  {(item?.logoUrl || displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl) ? (
                    <img
                      src={item?.logoUrl || displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl}
                      alt={displayData.display.organizationName || "Church logo"}
                      style={styles.logoLoopImage}
                    />
                  ) : (
                    <div style={styles.logoLoopFallback}>{displayData.display.organizationName || "Church"}</div>
                  )}
                </div>
              ) : isMembersSlide ? (
                <div style={{ ...styles.loopSlide, ...styles.loopInfoSlide, ...styles.membersLoopSlide }}>
                  <div style={styles.loopInfoTitle}>{item?.title || "Church Families"}</div>
                  {Array.isArray(item?.entries) && item.entries.length > 0 ? (
                    renderFamilyMirage(item, 10)
                  ) : (
                    <div style={styles.loopInfoBody}>{item?.body || "Family images will appear here."}</div>
                  )}
                </div>
              ) : isPreacherSlide ? (
                <div style={{ ...styles.loopSlide, ...styles.loopInfoSlide, ...styles.preacherLoopSlide }}>
                  {item?.imageUrl ? (
                    <img src={item.imageUrl} alt={item?.title || "Preacher"} style={styles.preacherLoopImage} />
                  ) : null}
                  <div style={styles.loopInfoTitle}>{item?.title || "Our Preacher"}</div>
                  {item?.subtitle ? (
                    <div style={styles.loopInfoSubtitle}>{item.subtitle}</div>
                  ) : null}
                  {item?.preacher?.bio || item?.body ? (
                    <div style={styles.preacherLoopBody}>{item?.preacher?.bio || item?.body}</div>
                  ) : null}
                  {item?.preacher?.email || item?.preacher?.phone ? (
                    <div style={styles.preacherLoopContact}>
                      {[item.preacher.email, item.preacher.phone].filter(Boolean).join(" • ")}
                    </div>
                  ) : null}
                </div>
              ) : isWelcomeSlide ? (
                <div style={{ ...styles.loopSlide, ...styles.loopSlideWelcome }}>
                  <div style={{ ...styles.welcomeContent, ...styles.welcomeContentCentered }}>
                    <div style={styles.welcomeTitle}>Welcome</div>
                    <div style={styles.welcomeChurchName}>
                      {item?.subtitle || displayData.display.organizationName || "Church"}
                    </div>
                    <div style={{ ...styles.welcomeBody, ...styles.welcomeBodyCentered }}>
                      {item?.body || "Service will begin shortly."}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    ...styles.loopSlide,
                    ...styles.loopInfoSlide,
                    ...(isEventsSlide ? styles.loopSlideEvents : {}),
                    ...(isCurrentHymnsSlide ? styles.loopSlideHymns : {}),
                    ...(hasNoActiveLoopSlides ? styles.loopSlideEmpty : {}),
                  }}
                >
                  <div style={{ ...styles.loopInfoTitle, ...(isCurrentHymnsSlide ? styles.loopInfoTitleHymns : {}) }}>{item?.title || "Welcome To"}</div>
                  {shouldShowLoopSubtitle ? (
                    <div style={styles.loopInfoSubtitle}>{item.subtitle}</div>
                  ) : null}
                  {renderLoopEntries(item) || (
                    <div style={styles.loopInfoBody}>{item?.body || ""}</div>
                  )}
                </div>
              )}
              {isLive && item?.itemType === "verse" ? (
                <div style={{ ...styles.liveVerseReference, ...getOneLineVerseReferenceStyle(item.title) }}>
                  {item.title}
                </div>
              ) : null}
            </motion.div>
          </AnimatePresence>
          {!isLiveHymn ? (
            <div style={styles.displayBrandCluster}>
              <img src={ChurchIcon} alt="Oikos Church" style={styles.displayCornerIcon} />
            </div>
          ) : null}
          <div style={styles.topBadgeCluster}>
            {sourceLabel ? (
              <div style={styles.sourceBadge}>
                <span>{sourceLabel}</span>
                {!isLive ? <RotateCcw size={13} strokeWidth={2.8} /> : null}
              </div>
            ) : null}
          </div>
          {loopKickerLabel ? (
            <div style={styles.loopKickerBadge}>{loopKickerLabel}</div>
          ) : null}
          {isLive && !isLiveHymn ? (
            <>
              {(displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl) ? (
                <img
                  src={displayData.display.organizationLogoUrl || cachedChurchBranding.logoUrl}
                  alt={displayData.display.organizationName || "Church logo"}
                  style={styles.liveServiceFooterLogo}
                />
              ) : null}
              <div style={styles.liveServiceFooterText}>{liveServiceDateLabel}</div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const styles = {
  codeEntryPage: {
    alignItems: "center",
    background:
      "radial-gradient(circle at 18% 12%, rgba(var(--color-primary-rgb),0.18), transparent 32%), linear-gradient(135deg, #f8fafc, #eef8f5)",
    boxSizing: "border-box",
    color: "#000000",
    display: "flex",
    justifyContent: "flex-start",
    minHeight: "100dvh",
    padding: 20,
  },
  codeEntryCard: {
    alignItems: "center",
    background: "rgba(255,255,255,0.88)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 18,
    boxShadow: "0 24px 80px rgba(15,23,42,0.16)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    maxWidth: 440,
    padding: "28px 24px",
    textAlign: "center",
    width: "100%",
  },
  codeEntryLogo: {
    display: "block",
    maxHeight: 104,
    maxWidth: 240,
    objectFit: "contain",
    width: "72%",
  },
  codeEntryEyebrow: {
    color: "#000000",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  codeEntryTitle: {
    color: "#000000",
    fontSize: "clamp(28px, 5vw, 42px)",
    fontWeight: 950,
    lineHeight: 1.05,
    margin: 0,
  },
  codeEntryText: {
    color: "#000000",
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.45,
    maxWidth: 340,
  },
  codeEntryInput: {
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    boxSizing: "border-box",
    color: "#000000",
    font: "inherit",
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: "0.08em",
    outline: "none",
    padding: "14px 16px",
    textAlign: "center",
    textTransform: "uppercase",
    width: "100%",
  },
  codeEntryButton: {
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 999,
    color: "#000000",
    cursor: "pointer",
    font: "inherit",
    fontSize: 15,
    fontWeight: 900,
    minHeight: 48,
    padding: "0 24px",
    width: "100%",
  },
  sleepPage: {
    alignItems: "center",
    background: "#000000",
    boxSizing: "border-box",
    display: "flex",
    height: "100dvh",
    justifyContent: "center",
    overflow: "hidden",
    position: "fixed",
    width: "100vw",
  },
  sleepIconWrap: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    position: "relative",
  },
  sleepIcon: {
    animation: "oikosSleepBreath 4.6s ease-in-out infinite",
    display: "block",
    filter: "grayscale(1) brightness(1.6)",
    height: "clamp(82px, 12vw, 170px)",
    opacity: 0.22,
    objectFit: "contain",
    transformOrigin: "center",
    width: "clamp(82px, 12vw, 170px)",
  },
  sleepZzz: {
    animation: "oikosSleepZFloat 5.4s ease-in-out infinite",
    color: "rgba(255,255,255,0.34)",
    fontSize: "clamp(17px, 2.35vw, 32px)",
    fontWeight: 900,
    left: "64%",
    opacity: 0,
    position: "absolute",
    textShadow: "0 0 18px rgba(255,255,255,0.12)",
    top: "10%",
  },
  sleepZzzOne: {
    animationDelay: "0s",
  },
  sleepZzzTwo: {
    animationDelay: "1.55s",
    fontSize: "clamp(14px, 1.9vw, 26px)",
    left: "58%",
    top: "20%",
  },
  sleepZzzThree: {
    animationDelay: "3.05s",
    fontSize: "clamp(20px, 2.75vw, 38px)",
    left: "68%",
    top: "14%",
  },
  foyerPage: {
    alignItems: "center",
    background:
      "radial-gradient(circle at 18% 14%, rgba(var(--color-primary-rgb),0.18), transparent 30%), linear-gradient(135deg, #ffffff, #eef8f5)",
    justifyContent: "center",
    padding: "clamp(24px, 5vw, 72px)",
  },
  foyerPanel: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(14px, 2vh, 28px)",
    maxWidth: "min(980px, 86vw)",
    textAlign: "center",
  },
  foyerLogo: {
    display: "block",
    maxHeight: "min(32vh, 320px)",
    maxWidth: "min(58vw, 680px)",
    objectFit: "contain",
  },
  foyerIcon: {
    display: "block",
    height: "clamp(92px, 16vw, 210px)",
    objectFit: "contain",
    width: "clamp(92px, 16vw, 210px)",
  },
  foyerKicker: {
    color: "var(--color-primary-dark)",
    fontSize: "clamp(16px, 2.2vw, 28px)",
    fontWeight: 950,
    textTransform: "uppercase",
  },
  foyerTitle: {
    color: "#0f172a",
    fontSize: "clamp(48px, 9vw, 132px)",
    fontWeight: 850,
    lineHeight: 1,
  },
  foyerScreenName: {
    color: "#64748b",
    fontSize: "clamp(20px, 3vw, 40px)",
    fontWeight: 800,
  },
  interactiveSlideSurface: {
    cursor: "pointer",
    paddingBottom: "clamp(70px, 10vh, 128px)",
  },
  interactiveLoopSlide: {
    paddingTop: "clamp(54px, 8vh, 94px)",
  },
  interactiveSlideCornerIcon: {
    display: "block",
    height: "clamp(42px, 6vmin, 82px)",
    left: "clamp(16px, 2.4vw, 42px)",
    objectFit: "contain",
    opacity: 0.88,
    pointerEvents: "none",
    position: "absolute",
    top: "clamp(14px, 2.3vh, 32px)",
    width: "clamp(42px, 6vmin, 82px)",
    zIndex: 42,
  },
  interactiveSlideOrgLogo: {
    display: "block",
    height: "clamp(42px, 7vh, 92px)",
    left: "50%",
    maxWidth: "min(240px, 22vw)",
    objectFit: "contain",
    pointerEvents: "none",
    position: "absolute",
    top: "clamp(14px, 2.3vh, 32px)",
    transform: "translateX(-50%)",
    width: "auto",
    zIndex: 42,
  },
  interactiveTouchTag: {
    alignItems: "center",
    bottom: "clamp(20px, 3.2vh, 38px)",
    color: "rgba(37, 53, 45, 0.5)",
    display: "inline-flex",
    fontSize: "clamp(14px, 1.35vw, 22px)",
    fontWeight: 850,
    gap: "clamp(8px, 0.8vw, 14px)",
    justifyContent: "center",
    left: "50%",
    letterSpacing: "0.08em",
    lineHeight: 1,
    minHeight: "clamp(34px, 4.5vh, 52px)",
    padding: "0 clamp(16px, 2vw, 28px)",
    position: "absolute",
    textShadow: "0 1px 0 rgba(255,255,255,0.65)",
    textTransform: "uppercase",
    transform: "translateX(-50%)",
    whiteSpace: "nowrap",
    zIndex: 20,
  },
  interactiveTouchDot: {
    background: "rgba(95, 125, 77, 0.52)",
    border: "1px solid rgba(255,255,255,0.68)",
    borderRadius: 999,
    boxShadow: "0 0 0 6px rgba(95, 125, 77, 0.08), 0 8px 18px rgba(37, 53, 45, 0.12)",
    display: "inline-block",
    height: "clamp(9px, 0.85vw, 13px)",
    width: "clamp(9px, 0.85vw, 13px)",
  },
  interactiveMenuPage: {
    alignItems: "center",
    backgroundAttachment: "fixed",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.94), rgba(246,249,247,0.98)), #f7f9f7",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    boxSizing: "border-box",
    color: "#25352d",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: "100dvh",
    overflow: "auto",
    padding: "clamp(26px, 3.5vh, 44px) 0 clamp(34px, 5vh, 68px)",
    position: "relative",
  },
  interactiveMenuCornerIcon: {
    height: "clamp(42px, 5vw, 74px)",
    left: "clamp(16px, 2.5vw, 34px)",
    objectFit: "contain",
    opacity: 0.82,
    position: "fixed",
    top: "clamp(14px, 2.4vw, 30px)",
    width: "clamp(42px, 5vw, 74px)",
    zIndex: 4,
  },
  interactiveMenuHero: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    padding: "0 24px clamp(28px, 5vh, 58px)",
    width: "100%",
  },
  interactiveMenuLogo: {
    display: "block",
    maxHeight: "clamp(90px, 13vh, 154px)",
    maxWidth: 420,
    objectFit: "contain",
    width: "min(42vw, 420px)",
  },
  interactiveMenuDivider: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    margin: "0 auto clamp(24px, 3vh, 34px)",
    maxWidth: 1320,
    padding: "0 32px",
    width: "100%",
  },
  interactiveMenuDividerLine: {
    background: "linear-gradient(90deg, transparent, rgba(95, 125, 77, 0.34), rgba(215, 224, 211, 0.86), rgba(95, 125, 77, 0.34), transparent)",
    borderRadius: 999,
    boxShadow: "0 1px 0 rgba(255,255,255,0.8)",
    display: "block",
    height: 1,
    width: "100%",
  },
  interactiveMenuGrid: {
    boxSizing: "border-box",
    display: "grid",
    gap: "clamp(26px, 3vw, 48px) clamp(24px, 3.4vw, 54px)",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    maxWidth: 1120,
    padding: "0 clamp(28px, 5vw, 72px)",
    width: "100%",
  },
  interactiveMenuTile: {
    alignItems: "center",
    background: "transparent",
    border: 0,
    borderRadius: 8,
    boxShadow: "none",
    color: "inherit",
    cursor: "pointer",
    display: "grid",
    font: "inherit",
    gap: "clamp(12px, 1.6vw, 18px)",
    justifyItems: "center",
    minHeight: "clamp(178px, 21vh, 238px)",
    padding: 0,
    textAlign: "center",
    touchAction: "manipulation",
  },
  interactiveMenuIconFrame: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(218, 228, 222, 0.86)",
    borderRadius: 8,
    boxSizing: "border-box",
    boxShadow: "0 22px 46px rgba(31, 45, 39, 0.11)",
    color: "#5f7d4d",
    display: "flex",
    height: "clamp(136px, 12vw, 184px)",
    justifyContent: "center",
    padding: "clamp(20px, 2vw, 28px)",
    width: "clamp(136px, 12vw, 184px)",
  },
  interactiveMenuIconImage: {
    display: "block",
    height: "100%",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
  },
  interactiveMenuTileCopy: {
    display: "grid",
    gap: 4,
    justifyItems: "center",
    minWidth: 0,
  },
  interactiveMenuTileTitle: {
    alignItems: "center",
    color: "#223327",
    display: "flex",
    fontSize: "clamp(19px, 1.7vw, 26px)",
    fontWeight: 900,
    justifyContent: "center",
    lineHeight: 1.12,
    textAlign: "center",
  },
  interactiveMenuTileSubtitle: {
    color: "#5f7066",
    fontSize: "clamp(15px, 1.1vw, 18px)",
    fontWeight: 550,
    lineHeight: 1.35,
  },
  interactiveAppPage: {
    background: "#ffffff",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    boxSizing: "border-box",
    color: "#0f172a",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(18px, 3vh, 34px)",
    minHeight: "100dvh",
    overflow: "auto",
    padding: "clamp(20px, 4vw, 58px)",
  },
  interactiveAppPageCompact: {
    gap: "clamp(8px, 1.4vh, 16px)",
    height: "100dvh",
    minHeight: "100dvh",
    overflow: "hidden",
    padding: "clamp(12px, 2.1vw, 28px)",
  },
  interactiveAppHeader: {
    alignItems: "start",
    display: "grid",
    gap: "clamp(14px, 2vw, 26px)",
    gridTemplateColumns: "minmax(0, 1fr) auto",
  },
  interactiveAppHeaderCompact: {
    alignItems: "center",
    gap: 12,
  },
  interactiveAppHeaderActions: {
    alignItems: "flex-end",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    justifySelf: "end",
  },
  interactiveAppLogo: {
    display: "block",
    maxHeight: 128,
    maxWidth: 190,
    objectFit: "contain",
    width: "100%",
  },
  interactiveAppHeading: {
    minWidth: 0,
  },
  interactiveAppKicker: {
    color: "var(--color-primary-dark)",
    fontSize: "clamp(12px, 1.2vw, 18px)",
    fontWeight: 950,
    textTransform: "uppercase",
  },
  interactiveAppTitleLarge: {
    color: "#0f172a",
    fontSize: "clamp(38px, 6vw, 92px)",
    fontWeight: 950,
    lineHeight: 1,
    marginTop: 6,
  },
  interactiveAppTitleCompact: {
    fontSize: "clamp(30px, min(5vw, 6vh), 62px)",
    marginTop: 0,
  },
  interactiveAppSubtitle: {
    color: "#64748b",
    fontSize: "clamp(17px, 2vw, 30px)",
    fontWeight: 800,
    marginTop: 8,
  },
  interactiveAppSubtitleCompact: {
    fontSize: "clamp(14px, min(1.7vw, 2.4vh), 22px)",
    marginTop: 4,
  },
  interactiveAppBody: {
    margin: "0 auto",
    maxWidth: 1240,
    width: "100%",
  },
  interactiveAppBodyCompact: {
    alignItems: "center",
    display: "flex",
    flex: "1 1 auto",
    justifyContent: "center",
    minHeight: 0,
  },
  interactiveAppFooter: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    marginTop: "auto",
    paddingTop: "clamp(8px, 2vh, 22px)",
  },
  interactiveAppFooterCompact: {
    flex: "0 0 auto",
    marginTop: 0,
    paddingTop: 0,
  },
  interactiveBottomBackButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d8e3db",
    borderRadius: 999,
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.08)",
    color: "#223327",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: "clamp(15px, 1.35vw, 20px)",
    fontWeight: 900,
    gap: 7,
    minHeight: 48,
    padding: "0 clamp(18px, 2.4vw, 30px)",
  },
  interactiveAppList: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  },
  interactiveAppListItem: {
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    display: "flex",
    gap: 14,
    minHeight: 110,
    padding: 14,
  },
  interactiveAppListImage: {
    borderRadius: 14,
    flex: "0 0 auto",
    height: 84,
    objectFit: "cover",
    width: 84,
  },
  interactiveAppListCopy: {
    minWidth: 0,
  },
  interactiveAppListTitle: {
    color: "#0f172a",
    fontSize: "clamp(19px, 2vw, 30px)",
    fontWeight: 900,
    lineHeight: 1.12,
  },
  interactiveAppListBody: {
    color: "#64748b",
    fontSize: "clamp(14px, 1.4vw, 20px)",
    fontWeight: 750,
    lineHeight: 1.42,
    marginTop: 7,
  },
  interactivePreacherCard: {
    alignItems: "center",
    display: "grid",
    gap: "clamp(20px, 4vw, 54px)",
    gridTemplateColumns: "minmax(190px, 0.72fr) minmax(0, 1.28fr)",
    margin: "0 auto",
    maxWidth: 1120,
    width: "100%",
  },
  interactivePreacherImage: {
    aspectRatio: "4 / 5",
    borderRadius: 24,
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.18)",
    display: "block",
    objectFit: "cover",
    width: "100%",
  },
  interactivePreacherImageFallback: {
    alignItems: "center",
    aspectRatio: "4 / 5",
    background: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(148, 163, 184, 0.38)",
    borderRadius: 24,
    color: "#64748b",
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  interactivePreacherCopy: {
    color: "#0f172a",
    display: "grid",
    gap: 14,
    minWidth: 0,
    textAlign: "left",
  },
  interactivePreacherName: {
    fontSize: "clamp(42px, 6vw, 92px)",
    fontWeight: 900,
    lineHeight: 0.95,
  },
  interactivePreacherTitle: {
    color: "#475569",
    fontSize: "clamp(22px, 2.5vw, 34px)",
    fontWeight: 800,
  },
  interactivePreacherBio: {
    color: "rgba(15, 23, 42, 0.78)",
    fontSize: "clamp(18px, 2vw, 28px)",
    lineHeight: 1.35,
    whiteSpace: "pre-line",
  },
  interactivePreacherContact: {
    color: "#0f766e",
    fontSize: "clamp(16px, 1.8vw, 24px)",
    fontWeight: 900,
    overflowWrap: "anywhere",
  },
  interactiveDirectory: {
    display: "grid",
    gap: "clamp(22px, 3vh, 36px)",
  },
  interactiveDirectorySection: {
    display: "grid",
    gap: 14,
  },
  interactiveDirectorySectionHeader: {
    alignItems: "center",
    color: "#223327",
    display: "flex",
    fontSize: "clamp(22px, 2.4vw, 34px)",
    fontWeight: 950,
    justifyContent: "space-between",
    lineHeight: 1.1,
  },
  interactiveDirectoryFamilyGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 520px), 1fr))",
  },
  interactiveDirectoryMemberGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  },
  interactiveDirectoryFamilyCard: {
    alignItems: "stretch",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    boxShadow: "0 18px 44px rgba(15,23,42,0.12)",
    color: "inherit",
    cursor: "pointer",
    display: "grid",
    font: "inherit",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
    minHeight: 286,
    overflow: "hidden",
    padding: 14,
    textAlign: "left",
  },
  interactiveDirectoryMemberCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    boxShadow: "0 18px 44px rgba(15,23,42,0.12)",
    display: "flex",
    gap: 12,
    minHeight: 104,
    padding: 12,
  },
  interactiveDirectoryFamilyPhoto: {
    alignItems: "center",
    background: "#e8f1ea",
    borderRadius: 14,
    color: "#54745a",
    display: "flex",
    flex: "0 0 auto",
    height: "100%",
    justifyContent: "center",
    objectFit: "cover",
    width: "100%",
  },
  interactiveDirectoryFamilyPhotoPanel: {
    borderRadius: 14,
    minHeight: 258,
    overflow: "hidden",
    position: "relative",
  },
  interactiveDirectoryFamilyOverlay: {
    background: "linear-gradient(180deg, rgba(15,23,42,0.78), rgba(15,23,42,0.46) 62%, rgba(15,23,42,0))",
    boxSizing: "border-box",
    color: "#ffffff",
    left: 0,
    padding: "16px 18px 34px",
    position: "absolute",
    right: 0,
    top: 0,
  },
  interactiveDirectoryOverlayName: {
    fontSize: "clamp(22px, 2.2vw, 34px)",
    fontWeight: 950,
    lineHeight: 1.04,
    overflowWrap: "anywhere",
    textShadow: "0 2px 10px rgba(0,0,0,0.28)",
  },
  interactiveDirectoryOverlayAddress: {
    alignItems: "center",
    display: "flex",
    fontSize: "clamp(13px, 1.1vw, 17px)",
    fontWeight: 850,
    gap: 7,
    lineHeight: 1.25,
    marginTop: 7,
    textShadow: "0 2px 8px rgba(0,0,0,0.24)",
  },
  interactiveDirectoryFamilyRoster: {
    alignContent: "start",
    background: "#f8fafc",
    border: "1px solid #e1e8f0",
    borderRadius: 14,
    display: "grid",
    gap: 10,
    padding: 14,
  },
  interactiveDirectoryRosterHeader: {
    alignItems: "center",
    color: "#0f172a",
    display: "flex",
    fontSize: "clamp(15px, 1.25vw, 20px)",
    fontWeight: 950,
    justifyContent: "space-between",
    lineHeight: 1.1,
  },
  interactiveDirectoryMemberPhoto: {
    alignItems: "center",
    background: "#e8f1ea",
    borderRadius: 999,
    color: "#54745a",
    display: "flex",
    flex: "0 0 auto",
    height: 74,
    justifyContent: "center",
    objectFit: "cover",
    width: 74,
  },
  interactiveDirectoryPhotoFallback: {
    border: "1px solid #d2dfd3",
  },
  interactiveDirectoryCopy: {
    flex: 1,
    minWidth: 0,
  },
  interactiveDirectoryName: {
    color: "#0f172a",
    fontSize: "clamp(18px, 1.7vw, 25px)",
    fontWeight: 950,
    lineHeight: 1.14,
  },
  interactiveDirectoryLine: {
    alignItems: "center",
    color: "#52615a",
    display: "flex",
    fontSize: "clamp(14px, 1.2vw, 18px)",
    fontWeight: 760,
    gap: 7,
    lineHeight: 1.3,
    marginTop: 6,
  },
  interactiveDirectoryMuted: {
    color: "#7a887f",
    fontSize: "clamp(13px, 1.1vw, 17px)",
    fontWeight: 800,
    lineHeight: 1.3,
    marginTop: 5,
  },
  interactiveDirectoryFamilyMembers: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "1fr",
    marginTop: 0,
  },
  interactiveDirectoryFamilyMember: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 14,
    boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
    display: "flex",
    gap: 8,
    minHeight: 40,
    minWidth: 0,
    padding: "4px 10px 4px 5px",
  },
  interactiveDirectoryFamilyMemberImage: {
    background: "#e8f1ea",
    borderRadius: 999,
    flex: "0 0 auto",
    height: 32,
    objectFit: "cover",
    width: 32,
  },
  interactiveDirectoryFamilyMemberInitials: {
    alignItems: "center",
    background: "#e8f1ea",
    borderRadius: 999,
    color: "#54745a",
    display: "flex",
    flex: "0 0 auto",
    fontSize: 11,
    fontWeight: 950,
    height: 32,
    justifyContent: "center",
    width: 32,
  },
  interactiveDirectoryFamilyMemberText: {
    display: "grid",
    gap: 2,
    minWidth: 0,
  },
  interactiveDirectoryFamilyMemberName: {
    color: "#33423a",
    display: "block",
    fontSize: "clamp(13px, 1.1vw, 17px)",
    fontWeight: 850,
    lineHeight: 1.12,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  interactiveDirectoryFamilyMemberRole: {
    color: "#718075",
    display: "block",
    fontSize: "clamp(10px, 0.9vw, 13px)",
    fontWeight: 850,
    lineHeight: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  interactiveFamilyDetail: {
    display: "grid",
    gap: "clamp(18px, 3vh, 34px)",
  },
  interactiveFamilyHero: {
    borderRadius: 24,
    boxShadow: "0 26px 58px rgba(15,23,42,0.16)",
    minHeight: "clamp(280px, 38vh, 460px)",
    overflow: "hidden",
    position: "relative",
  },
  interactiveFamilyHeroPhoto: {
    alignItems: "center",
    background: "#e8f1ea",
    color: "#54745a",
    display: "flex",
    height: "100%",
    justifyContent: "center",
    left: 0,
    objectFit: "cover",
    position: "absolute",
    top: 0,
    width: "100%",
  },
  interactiveFamilyHeroOverlay: {
    background: "linear-gradient(180deg, rgba(15,23,42,0.72), rgba(15,23,42,0.2) 44%, rgba(15,23,42,0.76))",
    boxSizing: "border-box",
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    inset: 0,
    justifyContent: "space-between",
    padding: "clamp(16px, 2.5vw, 30px)",
    position: "absolute",
  },
  interactiveFamilyInlineBack: {
    alignItems: "center",
    alignSelf: "flex-start",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 999,
    color: "#223327",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: "clamp(14px, 1.3vw, 19px)",
    fontWeight: 950,
    gap: 6,
    padding: "10px 14px 10px 10px",
  },
  interactiveFamilyHeroCopy: {
    display: "grid",
    gap: 10,
    maxWidth: "min(920px, 92%)",
  },
  interactiveFamilyHeroTitle: {
    color: "#ffffff",
    fontSize: "clamp(44px, min(6.6vw, 8.4vh), 96px)",
    fontWeight: 950,
    lineHeight: 0.98,
    margin: 0,
    overflowWrap: "anywhere",
    textShadow: "0 4px 24px rgba(0,0,0,0.42)",
  },
  interactiveFamilyHeroAddress: {
    alignItems: "center",
    display: "flex",
    fontSize: "clamp(18px, 2vw, 30px)",
    fontWeight: 900,
    gap: 10,
    lineHeight: 1.2,
    textShadow: "0 3px 16px rgba(0,0,0,0.38)",
  },
  interactiveFamilyHeroAnniversary: {
    alignItems: "center",
    display: "flex",
    fontSize: "clamp(16px, 1.7vw, 25px)",
    fontWeight: 850,
    gap: 10,
    lineHeight: 1.25,
    textShadow: "0 3px 16px rgba(0,0,0,0.38)",
  },
  interactiveFamilyMemberSection: {
    display: "grid",
    gap: 14,
  },
  interactiveFamilyLargeMemberGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
  },
  interactiveFamilyLargeMemberCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 22,
    boxShadow: "0 18px 44px rgba(15,23,42,0.12)",
    display: "flex",
    gap: 16,
    minHeight: 154,
    padding: 14,
  },
  interactiveFamilyLargeMemberImage: {
    background: "#e8f1ea",
    borderRadius: 20,
    flex: "0 0 auto",
    height: "clamp(118px, 14vw, 168px)",
    objectFit: "cover",
    width: "clamp(118px, 14vw, 168px)",
  },
  interactiveFamilyLargeMemberInitials: {
    alignItems: "center",
    background: "#e8f1ea",
    border: "1px solid #d2dfd3",
    borderRadius: 20,
    color: "#54745a",
    display: "flex",
    flex: "0 0 auto",
    fontSize: "clamp(34px, 4vw, 56px)",
    fontWeight: 950,
    height: "clamp(118px, 14vw, 168px)",
    justifyContent: "center",
    width: "clamp(118px, 14vw, 168px)",
  },
  interactiveFamilyLargeMemberCopy: {
    display: "grid",
    gap: 7,
    minWidth: 0,
  },
  interactiveFamilyLargeMemberName: {
    color: "#0f172a",
    fontSize: "clamp(24px, 2.5vw, 38px)",
    fontWeight: 950,
    lineHeight: 1.05,
    overflowWrap: "anywhere",
  },
  interactiveFamilyLargeMemberRole: {
    color: "#54745a",
    fontSize: "clamp(17px, 1.65vw, 25px)",
    fontWeight: 900,
    lineHeight: 1.15,
  },
  interactiveFamilyLargeMemberMeta: {
    color: "#64748b",
    fontSize: "clamp(14px, 1.35vw, 19px)",
    fontWeight: 760,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
  },
  interactiveFamilyLargeMemberMetaLine: {
    alignItems: "center",
    color: "#4b6475",
    display: "flex",
    fontSize: "clamp(14px, 1.35vw, 19px)",
    fontWeight: 820,
    gap: 8,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
  },
  interactiveAppEmpty: {
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 18,
    color: "#64748b",
    fontSize: "clamp(18px, 2vw, 28px)",
    fontWeight: 800,
    lineHeight: 1.45,
    padding: "clamp(22px, 4vw, 48px)",
    textAlign: "center",
  },
  interactiveForm: {
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 20,
    display: "grid",
    gap: 14,
    margin: "0 auto",
    maxWidth: 720,
    padding: "clamp(18px, 3vw, 34px)",
  },
  interactiveInput: {
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    boxSizing: "border-box",
    color: "#0f172a",
    font: "inherit",
    fontSize: "clamp(18px, 2vw, 26px)",
    fontWeight: 800,
    minHeight: 58,
    padding: "0 16px",
    width: "100%",
  },
  interactiveTextarea: {
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    boxSizing: "border-box",
    color: "#0f172a",
    font: "inherit",
    fontSize: "clamp(18px, 2vw, 26px)",
    fontWeight: 800,
    padding: 16,
    resize: "vertical",
    width: "100%",
  },
  interactiveSubmitButton: {
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    font: "inherit",
    fontSize: "clamp(17px, 1.7vw, 24px)",
    fontWeight: 900,
    minHeight: 58,
    padding: "0 24px",
  },
  interactiveFormNotice: {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    borderRadius: 14,
    color: "#166534",
    fontSize: "clamp(15px, 1.5vw, 22px)",
    fontWeight: 850,
    padding: 14,
    textAlign: "center",
  },
  interactiveDutiesGrid: {
    display: "grid",
    gap: "clamp(18px, 3vw, 34px)",
    gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 0.85fr)",
  },
  interactiveDutiesPanel: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 22,
    display: "grid",
    gap: 14,
    padding: "clamp(18px, 3vw, 34px)",
  },
  interactiveDutiesPanelTitle: {
    color: "#0f172a",
    fontSize: "clamp(30px, 4vw, 56px)",
    fontWeight: 950,
    lineHeight: 1,
  },
  interactiveDutiesPanelSubtitle: {
    color: "var(--color-primary-dark)",
    fontSize: "clamp(16px, 1.8vw, 24px)",
    fontWeight: 900,
  },
  interactiveDutiesList: {
    display: "grid",
    gap: 10,
    marginTop: 6,
  },
  interactiveDutiesRow: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    padding: "14px 16px",
  },
  interactiveDutiesRole: {
    color: "#475569",
    fontSize: "clamp(16px, 1.7vw, 24px)",
    fontWeight: 900,
  },
  interactiveDutiesName: {
    color: "#0f172a",
    fontSize: "clamp(18px, 2vw, 28px)",
    fontWeight: 950,
    textAlign: "right",
  },
  phoneQrGrid: {
    display: "grid",
    gap: "clamp(8px, 1.4vh, 18px)",
    gridTemplateColumns: "minmax(240px, min(480px, 86vw))",
    justifyContent: "center",
    width: "100%",
  },
  phoneQrCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d6e8e2",
    borderRadius: 22,
    boxShadow: "0 18px 44px rgba(15,23,42,0.12)",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(8px, 1.4vh, 14px)",
    minHeight: 0,
    padding: "clamp(12px, 2vw, 24px)",
    textAlign: "center",
  },
  phoneQrImage: {
    aspectRatio: "1 / 1",
    background: "#ffffff",
    border: "10px solid #ffffff",
    borderRadius: 18,
    boxShadow: "0 0 0 1px #d9e1ef, 0 12px 28px rgba(15,23,42,0.12)",
    display: "block",
    maxHeight: "min(43vh, 310px)",
    maxWidth: "min(43vh, 310px)",
    objectFit: "contain",
    width: "100%",
  },
  phoneQrPlaceholder: {
    alignItems: "center",
    aspectRatio: "1 / 1",
    background:
      "linear-gradient(90deg, #0f172a 10px, transparent 10px) 0 0 / 34px 34px, linear-gradient(#0f172a 10px, transparent 10px) 0 0 / 34px 34px, #f8fafc",
    border: "10px solid #ffffff",
    borderRadius: 18,
    boxShadow: "0 0 0 1px #d9e1ef, 0 12px 28px rgba(15,23,42,0.12)",
    color: "#0f172a",
    display: "flex",
    fontSize: "clamp(28px, 4vw, 54px)",
    fontWeight: 950,
    justifyContent: "center",
    maxWidth: 260,
    width: "100%",
  },
  phoneQrTitle: {
    color: "#0f172a",
    fontSize: "clamp(20px, min(2.2vw, 3.2vh), 32px)",
    fontWeight: 950,
    lineHeight: 1.08,
  },
  phoneQrBody: {
    color: "#64748b",
    fontSize: "clamp(13px, min(1.3vw, 2vh), 18px)",
    fontWeight: 800,
    lineHeight: 1.25,
    maxWidth: 360,
  },
  phoneQrLink: {
    background: "#f1f5f9",
    border: "1px solid #dbe5f0",
    borderRadius: 999,
    color: "#0f172a",
    fontSize: "clamp(11px, min(1vw, 1.55vh), 15px)",
    fontWeight: 850,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    padding: "8px 12px",
  },
  preparingPage: {
    alignItems: "center",
    background:
      "radial-gradient(circle at 50% 42%, rgba(var(--color-primary-rgb),0.18), transparent 31%), #000000",
    boxSizing: "border-box",
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(14px, 2.4vh, 28px)",
    height: "100dvh",
    justifyContent: "center",
    overflow: "hidden",
    padding: "clamp(28px, 6vw, 92px)",
    position: "fixed",
    textAlign: "center",
    width: "100vw",
  },
  preparingIconWrap: {
    alignItems: "center",
    display: "flex",
    height: "clamp(168px, 24vw, 340px)",
    justifyContent: "center",
    position: "relative",
    width: "clamp(168px, 24vw, 340px)",
  },
  preparingRing: {
    border: "1px solid rgba(255,255,255,0.48)",
    borderRadius: "50%",
    height: "116%",
    left: "50%",
    pointerEvents: "none",
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "116%",
    zIndex: 0,
  },
  preparingRingOne: {
    animation: "oikosPreparingRing 2.35s cubic-bezier(0.18, 0.78, 0.16, 1) infinite",
  },
  preparingRingTwo: {
    animation: "oikosPreparingRing 2.35s cubic-bezier(0.18, 0.78, 0.16, 1) 1.15s infinite",
    borderColor: "rgba(var(--color-primary-rgb),0.52)",
  },
  preparingOrbit: {
    animation: "oikosPreparingOrbit 2.8s linear infinite",
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: "50%",
    height: "92%",
    position: "absolute",
    width: "92%",
    zIndex: 1,
  },
  preparingOrbitDot: {
    background: "rgba(var(--color-primary-rgb),0.98)",
    borderRadius: "50%",
    boxShadow: "0 0 22px rgba(var(--color-primary-rgb),0.86)",
    display: "block",
    height: "clamp(10px, 1.4vw, 18px)",
    left: "50%",
    position: "absolute",
    top: "-7px",
    transform: "translateX(-50%)",
    width: "clamp(10px, 1.4vw, 18px)",
  },
  preparingCore: {
    alignItems: "center",
    animation: "oikosPreparingCore 1.65s ease-in-out infinite",
    background: "#ffffff",
    border: "1px solid rgba(255,255,255,0.88)",
    borderRadius: "50%",
    display: "flex",
    height: "54%",
    justifyContent: "center",
    position: "relative",
    width: "54%",
    zIndex: 2,
  },
  preparingIcon: {
    display: "block",
    height: "68%",
    objectFit: "contain",
    width: "68%",
  },
  preparingTitle: {
    animation: "oikosPreparingText 1.7s ease-in-out infinite",
    fontSize: "clamp(28px, 4.8vw, 82px)",
    fontWeight: 950,
    lineHeight: 1,
  },
  preparingDetail: {
    color: "rgba(255,255,255,0.68)",
    fontSize: "clamp(14px, 1.6vw, 24px)",
    fontWeight: 850,
  },
  identifyPage: {
    alignItems: "center",
    background:
      "radial-gradient(circle at 50% 38%, rgba(var(--color-primary-rgb),0.28), transparent 30%), radial-gradient(circle at 50% 52%, rgba(255,255,255,0.11), transparent 36%), #020617",
    boxSizing: "border-box",
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(12px, 2.2vh, 28px)",
    height: "100dvh",
    justifyContent: "center",
    overflow: "hidden",
    padding: "clamp(28px, 5vw, 84px)",
    position: "fixed",
    textAlign: "center",
    width: "100vw",
  },
  identifyBeaconWrap: {
    alignItems: "center",
    display: "flex",
    height: "clamp(180px, 27vw, 360px)",
    justifyContent: "center",
    marginBottom: "clamp(4px, 1vh, 14px)",
    position: "relative",
    width: "clamp(180px, 27vw, 360px)",
  },
  identifyBeaconRing: {
    border: "2px solid rgba(255,255,255,0.72)",
    borderRadius: "50%",
    boxShadow: "0 0 52px rgba(var(--color-primary-rgb),0.16)",
    height: "max(126vw, 126vh)",
    left: "50%",
    pointerEvents: "none",
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "max(126vw, 126vh)",
    zIndex: 0,
  },
  identifyBeaconRingInner: {
    animation: "oikosIdentifyRingPulse 2.2s cubic-bezier(0.18, 0.78, 0.16, 1) infinite",
  },
  identifyBeaconRingOuter: {
    animation: "oikosIdentifyRingPulse 2.2s cubic-bezier(0.18, 0.78, 0.16, 1) 1.1s infinite",
    borderColor: "rgba(255,255,255,0.54)",
  },
  identifyBeaconGlow: {
    animation: "oikosIdentifyBeaconGlow 1.3s ease-in-out infinite",
    background: "rgba(var(--color-primary-rgb),0.92)",
    borderRadius: 999,
    bottom: "8%",
    filter: "blur(16px)",
    height: "clamp(14px, 2vw, 28px)",
    left: "50%",
    position: "absolute",
    width: "68%",
    zIndex: 1,
  },
  identifyBeaconCore: {
    alignItems: "center",
    animation: "oikosIdentifyCorePulse 1.3s ease-in-out infinite",
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(255,255,255,0.92)",
    borderRadius: "50%",
    display: "flex",
    height: "58%",
    justifyContent: "center",
    position: "relative",
    width: "58%",
    zIndex: 2,
  },
  identifyIcon: {
    display: "block",
    height: "68%",
    objectFit: "contain",
    width: "68%",
  },
  identifyKicker: {
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.34)",
    borderRadius: 999,
    color: "rgba(255,255,255,0.86)",
    fontSize: "clamp(13px, 1.45vw, 24px)",
    fontWeight: 950,
    letterSpacing: "0.12em",
    padding: "0.48em 0.94em",
    textTransform: "uppercase",
  },
  identifyName: {
    color: "#ffffff",
    fontSize: "clamp(44px, 10vw, 154px)",
    fontWeight: 950,
    lineHeight: 0.96,
    maxWidth: "92vw",
    overflowWrap: "anywhere",
    textShadow: "0 0 34px rgba(255,255,255,0.26)",
  },
  identifyCode: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "clamp(16px, 2vw, 32px)",
    fontWeight: 850,
    maxWidth: "88vw",
    overflowWrap: "anywhere",
  },
  page: {
    background: "#ffffff",
    color: "#000000",
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    inset: 0,
    overflow: "hidden",
    position: "fixed",
    width: "100vw",
  },
  viewport: {
    alignItems: "center",
    background: "#ffffff",
    display: "flex",
    flex: 1,
    height: "100%",
    justifyContent: "center",
    overflow: "hidden",
    padding: 0,
    width: "100%",
  },
  slideSurface: {
    alignItems: "center",
    background: "#ffffff",
    boxSizing: "border-box",
    display: "flex",
    flex: 1,
    height: "100%",
    justifyContent: "center",
    overflow: "hidden",
    padding: "clamp(4px, 0.8vh, 14px) clamp(28px, 6vw, 92px) clamp(28px, 5vh, 72px)",
    position: "relative",
    width: "100%",
  },
  liveClickableSlideSurface: {
    cursor: "pointer",
    userSelect: "none",
  },
  titleSlideSurface: {
    padding: 0,
  },
  liveVerseSlideSurface: {
    padding: 0,
  },
  liveHymnSlideSurface: {
    padding: "clamp(8px, 1.2vh, 22px) clamp(8px, 1.2vw, 22px)",
  },
  slideContent: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flex: 1,
    height: "100%",
    justifyContent: "center",
    minHeight: 0,
    minWidth: 0,
    position: "relative",
    width: "100%",
  },
  liveHymnSlideContent: {
    inset: 0,
    padding: "clamp(8px, 1.2vh, 22px) clamp(8px, 1.2vw, 22px)",
    position: "absolute",
  },
  loopSlide: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
    justifyContent: "flex-start",
    margin: "0 auto",
    maxHeight: "100%",
    maxWidth: "min(1280px, 92vw)",
    overflow: "hidden",
    paddingTop: "clamp(72px, 12vh, 136px)",
    textAlign: "center",
  },
  loopSlideWelcome: {
    maxWidth: "min(1560px, 92vw)",
    justifyContent: "center",
    paddingTop: "clamp(70px, 11vh, 128px)",
  },
  loopSlideEvents: {
    paddingTop: "clamp(58px, 8.8vh, 104px)",
  },
  loopSlideEmpty: {
    paddingTop: "clamp(58px, 8.8vh, 104px)",
  },
  loopInfoSlide: {
    maxWidth: "min(1320px, 90vw)",
    paddingTop: "clamp(54px, 8vh, 94px)",
  },
  preacherLoopSlide: {
    justifyContent: "center",
    paddingTop: "clamp(34px, 5vh, 64px)",
  },
  preacherLoopImage: {
    borderRadius: "50%",
    boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
    height: "clamp(150px, 24vh, 270px)",
    marginBottom: "clamp(18px, 2.8vh, 38px)",
    objectFit: "cover",
    width: "clamp(150px, 24vh, 270px)",
  },
  preacherLoopBody: {
    color: "rgba(15, 23, 42, 0.72)",
    fontSize: "clamp(24px, min(3vw, 4.5vh), 54px)",
    fontWeight: 500,
    lineHeight: 1.18,
    marginTop: "clamp(18px, 2.4vh, 34px)",
    maxWidth: "min(1040px, 82vw)",
    whiteSpace: "pre-line",
  },
  preacherLoopContact: {
    color: "#0f766e",
    fontSize: "clamp(20px, min(2.2vw, 3.3vh), 38px)",
    fontWeight: 900,
    marginTop: "clamp(16px, 2vh, 28px)",
    overflowWrap: "anywhere",
  },
  loopSlideHymns: {
    maxWidth: "min(1220px, 88vw)",
    paddingBottom: "clamp(4px, 1vh, 14px)",
    paddingTop: "clamp(54px, 8vh, 96px)",
  },
  liveServiceHeaderSlide: {
    justifyContent: "center",
    paddingBottom: "clamp(72px, 12vh, 136px)",
    paddingTop: 0,
  },
  liveServiceSongSlide: {
    paddingBottom: "clamp(44px, 8vh, 96px)",
    paddingLeft: "clamp(24px, 5vw, 80px)",
    paddingRight: "clamp(24px, 5vw, 80px)",
  },
  liveServiceSongTitle: {
    maxWidth: "min(1480px, 92vw)",
    overflow: "visible",
    overflowWrap: "anywhere",
    textAlign: "center",
    textOverflow: "clip",
    whiteSpace: "normal",
    wordBreak: "normal",
  },
  liveServiceSongNumber: {
    fontSize: "clamp(48px, min(8vw, 12vh), 138px)",
    lineHeight: 0.95,
    marginTop: "clamp(18px, 3.2vh, 46px)",
    overflowWrap: "anywhere",
    textAlign: "center",
    whiteSpace: "normal",
  },
  liveServiceInvitationSongDetails: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(12px, 2vh, 28px)",
    marginTop: "clamp(18px, 3.2vh, 46px)",
    maxWidth: "min(1280px, 88vw)",
    textAlign: "center",
  },
  liveServiceInvitationSongNumber: {
    color: "#000000",
    fontSize: "clamp(72px, min(12vw, 17vh), 210px)",
    fontWeight: 950,
    lineHeight: 0.92,
    overflowWrap: "anywhere",
  },
  liveServiceInvitationSongName: {
    color: "#000000",
    fontSize: "clamp(34px, min(5.4vw, 7.2vh), 92px)",
    fontWeight: 850,
    lineHeight: 1.05,
    overflowWrap: "anywhere",
  },
  liveServiceClockTime: {
    color: "#000000",
    fontSize: "clamp(96px, min(16vw, 25vh), 280px)",
    fontWeight: 400,
    lineHeight: 0.9,
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  liveServiceClockLabel: {
    color: "rgba(15, 23, 42, 0.58)",
    fontSize: "clamp(24px, min(3.2vw, 5.4vh), 58px)",
    fontWeight: 400,
    lineHeight: 1.1,
    marginTop: "clamp(18px, 3vh, 42px)",
    textAlign: "center",
  },
  liveServiceLogoSlide: {
    paddingBottom: "clamp(42px, 8vh, 104px)",
  },
  liveServiceFeatureLogo: {
    display: "block",
    maxHeight: "min(28vh, 260px)",
    maxWidth: "min(42vw, 520px)",
    objectFit: "contain",
    width: "auto",
  },
  liveServiceLogoTitle: {
    color: "rgba(15, 23, 42, 0.62)",
    fontSize: "clamp(28px, min(4vw, 6vh), 70px)",
    fontWeight: 400,
    lineHeight: 1,
    marginTop: "clamp(28px, 5vh, 72px)",
    textAlign: "center",
  },
  liveServiceOrganizationName: {
    color: "#000000",
    fontSize: "clamp(54px, min(8.2vw, 12vh), 148px)",
    fontWeight: 400,
    lineHeight: 0.96,
    marginTop: "clamp(12px, 2.2vh, 32px)",
    maxWidth: "min(1280px, 92vw)",
    textAlign: "center",
    textWrap: "balance",
  },
  liveServiceThankYouTitle: {
    color: "#000000",
    fontSize: "clamp(48px, min(7.5vw, 11vh), 138px)",
    fontWeight: 400,
    lineHeight: 0.98,
    marginTop: "clamp(30px, 5vh, 76px)",
    maxWidth: "min(1280px, 92vw)",
    textAlign: "center",
    textWrap: "balance",
  },
  logoLoopSlide: {
    justifyContent: "center",
    maxWidth: "min(1420px, 92vw)",
    paddingBottom: "clamp(72px, 13vh, 160px)",
    paddingTop: "clamp(52px, 8vh, 96px)",
  },
  logoLoopImage: {
    display: "block",
    maxHeight: "min(58vh, 540px)",
    maxWidth: "min(62vw, 760px)",
    objectFit: "contain",
    width: "auto",
  },
  liveServiceFooterLogo: {
    bottom: "clamp(24px, 4vh, 52px)",
    display: "block",
    left: "clamp(28px, 4vw, 72px)",
    maxHeight: "clamp(54px, 9vh, 118px)",
    maxWidth: "clamp(130px, 19vw, 270px)",
    objectFit: "contain",
    position: "absolute",
    zIndex: 6,
  },
  liveServiceFooterText: {
    bottom: "clamp(28px, 4vh, 56px)",
    color: "rgba(15, 23, 42, 0.42)",
    fontSize: "clamp(14px, 1.45vw, 26px)",
    fontWeight: 850,
    letterSpacing: "0.04em",
    position: "absolute",
    right: "clamp(28px, 4vw, 72px)",
    textTransform: "uppercase",
    zIndex: 6,
  },
  logoLoopFallback: {
    color: "#0f172a",
    fontSize: "clamp(42px, min(7vw, 12vh), 132px)",
    fontWeight: 400,
    lineHeight: 1,
    textAlign: "center",
    textWrap: "balance",
  },
  membersLoopSlide: {
    justifyContent: "flex-start",
    maxWidth: "min(1500px, 94vw)",
    paddingBottom: "clamp(16px, 3vh, 42px)",
  },
  familyMirage: {
    height: "clamp(430px, 60vh, 690px)",
    marginTop: "clamp(8px, 1.5vh, 24px)",
    maxWidth: "min(1500px, 94vw)",
    position: "relative",
    width: "100%",
  },
  familyMiragePhoto: {
    background: "#ffffff",
    border: "clamp(3px, 0.4vw, 6px) solid #ffffff",
    borderRadius: "clamp(12px, 1.35vw, 22px)",
    boxShadow: "0 20px 42px rgba(15, 23, 42, 0.18)",
    overflow: "hidden",
    position: "absolute",
  },
  familyMirageImage: {
    background: "#f8fafc",
    display: "block",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    width: "100%",
  },
  familyMirageInfo: {
    background: "linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(15, 23, 42, 0.72))",
    bottom: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    left: 0,
    minHeight: "42%",
    padding: "clamp(22px, 3.2vh, 38px) clamp(10px, 1.2vw, 16px) clamp(8px, 1.1vh, 13px)",
    position: "absolute",
    right: 0,
  },
  familyMirageCaption: {
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    boxSizing: "border-box",
    color: "#ffffff",
    display: "-webkit-box",
    flex: "0 0 auto",
    fontSize: "clamp(15px, min(1.45vw, 2.25vh), 25px)",
    fontWeight: 950,
    lineHeight: 1.08,
    overflow: "hidden",
    textShadow: "0 2px 12px rgba(0, 0, 0, 0.46)",
    padding: 0,
    textAlign: "center",
    whiteSpace: "normal",
  },
  familyMirageMembers: {
    display: "grid",
    gap: "clamp(3px, 0.45vh, 5px)",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  familyMirageMember: {
    alignItems: "center",
    display: "flex",
    gap: "clamp(4px, 0.6vw, 7px)",
    minWidth: 0,
  },
  familyMirageMemberImage: {
    background: "#e8f1ea",
    borderRadius: 999,
    flex: "0 0 auto",
    height: "clamp(18px, min(2.1vw, 2.8vh), 28px)",
    objectFit: "cover",
    width: "clamp(18px, min(2.1vw, 2.8vh), 28px)",
  },
  familyMirageMemberInitials: {
    alignItems: "center",
    background: "#e8f1ea",
    borderRadius: 999,
    color: "#54745a",
    display: "flex",
    flex: "0 0 auto",
    fontSize: "clamp(8px, 0.85vw, 12px)",
    fontWeight: 950,
    height: "clamp(18px, min(2.1vw, 2.8vh), 28px)",
    justifyContent: "center",
    width: "clamp(18px, min(2.1vw, 2.8vh), 28px)",
  },
  familyMirageMemberName: {
    color: "#52615a",
    display: "block",
    fontSize: "clamp(8px, min(0.9vw, 1.25vh), 12px)",
    fontWeight: 800,
    lineHeight: 1.1,
    minWidth: 0,
    overflow: "hidden",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  verseSlide: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    justifyContent: "center",
    margin: "0 auto",
    maxHeight: "100%",
    maxWidth: "min(1340px, 94vw)",
    overflow: "hidden",
    textAlign: "center",
  },
  liveVerseSlide: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "grid",
    flex: 1,
    gridTemplateRows: "calc(clamp(10px, 1.2vh, 18px) + clamp(96px, 16vh, 220px) + clamp(4px, 0.6vh, 10px)) minmax(0, 1fr) clamp(72px, 10vh, 126px)",
    height: "100%",
    justifyItems: "center",
    margin: "0 auto",
    maxHeight: "100%",
    maxWidth: "none",
    overflow: "hidden",
    padding: "0 clamp(44px, 4.4vw, 88px)",
    position: "relative",
    textAlign: "center",
    width: "100%",
  },
  liveVerseReference: {
    alignItems: "center",
    boxSizing: "border-box",
    color: "#000000",
    display: "flex",
    fontWeight: 400,
    height: "clamp(96px, 16vh, 220px)",
    justifyContent: "center",
    left: "clamp(48px, 7vw, 116px)",
    lineHeight: 1,
    minWidth: 0,
    overflow: "hidden",
    padding: "0 clamp(8px, 1.2vw, 22px) 0.04em",
    position: "absolute",
    right: "clamp(76px, 9vw, 168px)",
    textAlign: "center",
    textOverflow: "clip",
    top: "clamp(10px, 1.2vh, 18px)",
    whiteSpace: "nowrap",
    zIndex: 5,
  },
  liveVerseText: {
    alignItems: "stretch",
    boxSizing: "border-box",
    color: "#000000",
    display: "flex",
    flexDirection: "column",
    fontSize: "clamp(38px, min(5.4vw, 8.8vh), 96px)",
    fontWeight: 400,
    gap: "clamp(5px, 0.9vh, 16px)",
    gridRow: 2,
    height: "100%",
    justifyContent: "flex-start",
    lineHeight: 1.08,
    marginTop: 0,
    maxWidth: "none",
    minHeight: 0,
    overflow: "hidden",
    padding: "clamp(2px, 0.35vh, 6px) clamp(10px, 1.2vw, 24px) clamp(6px, 0.8vh, 12px)",
    textAlign: "left",
    width: "min(1540px, 100%)",
  },
  liveVerseLine: {
    boxSizing: "border-box",
    display: "block",
    justifyContent: "center",
    maxWidth: "100%",
    textAlign: "left",
    width: "100%",
  },
  liveVerseNumber: {
    color: "#000000",
    display: "inline-block",
    fontSize: "0.72em",
    fontWeight: 700,
    lineHeight: 1,
    marginRight: "0.14em",
    textAlign: "right",
    transform: "translateY(-0.18em)",
    verticalAlign: "baseline",
  },
  liveVerseBody: {
    boxSizing: "border-box",
    display: "inline",
    maxWidth: "none",
    minWidth: 0,
    textAlign: "center",
    textWrap: "balance",
    whiteSpace: "pre-line",
  },
  imageSlide: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    height: "100%",
    justifyContent: "center",
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
  imageSlideAsset: {
    display: "block",
    height: "100%",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
  },
  endOfSongBadge: {
    background: "rgba(254, 226, 226, 0.92)",
    border: "1px solid rgba(248, 113, 113, 0.38)",
    borderRadius: 999,
    bottom: 28,
    color: "#991b1b",
    fontSize: 13,
    fontWeight: 900,
    left: 28,
    letterSpacing: "0.08em",
    padding: "10px 14px",
    position: "absolute",
    textTransform: "uppercase",
  },
  endOfSongText: {
    color: "#991b1b",
    fontSize: "clamp(12px, 1vw, 16px)",
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginTop: 24,
    textTransform: "uppercase",
  },
  kicker: {
    color: "#000000",
    fontSize: "clamp(12px, 1.2vw, 18px)",
    fontWeight: 900,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: "clamp(44px, min(7.4vw, 13vh), 132px)",
    fontWeight: 400,
    lineHeight: 0.98,
    marginTop: 0,
  },
  hymnTextSlideTitle: {
    fontSize: "clamp(34px, min(6vw, 10vh), 104px)",
    fontWeight: 400,
    lineHeight: 1,
    maxWidth: "min(1280px, 92vw)",
    overflowWrap: "anywhere",
    textWrap: "balance",
  },
  hymnTextSlideBody: {
    fontSize: "clamp(22px, min(3.4vw, 5.8vh), 58px)",
    fontWeight: 400,
    lineHeight: 1.22,
    marginTop: "clamp(18px, 3vh, 42px)",
    maxHeight: "60vh",
    maxWidth: "min(1120px, 92vw)",
    overflow: "hidden",
    textWrap: "balance",
    whiteSpace: "pre-line",
  },
  titleSlide: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flex: 1,
    height: "100%",
    justifyContent: "center",
    overflow: "hidden",
    padding: "clamp(26px, 4.2vh, 68px) clamp(28px, 4vw, 76px)",
    position: "relative",
    textAlign: "center",
    width: "100%",
  },
  loopHeaderLogo: {
    display: "block",
    height: "clamp(92px, 17vh, 214px)",
    left: "50%",
    maxWidth: "min(760px, 58vw)",
    objectFit: "contain",
    position: "absolute",
    top: "clamp(16px, 2.6vh, 34px)",
    transform: "translateX(-50%)",
    width: "auto",
    zIndex: 5,
  },
  preServiceHeaderLogo: {
    bottom: "clamp(16px, 2.4vh, 36px)",
    height: "clamp(42px, 7vh, 92px)",
    left: "clamp(16px, 2.4vw, 42px)",
    maxWidth: "min(240px, 22vw)",
    pointerEvents: "none",
    top: "auto",
    transform: "none",
    zIndex: 40,
  },
  loopHeaderLogoHymns: {
    height: "clamp(76px, 14vh, 168px)",
    top: "clamp(12px, 2vh, 28px)",
  },
  liveHymnHeaderLogo: {
    bottom: "clamp(16px, 2.4vh, 36px)",
    height: "clamp(42px, 7vh, 92px)",
    left: "clamp(16px, 2.4vw, 42px)",
    maxWidth: "min(240px, 22vw)",
    pointerEvents: "none",
    top: "auto",
    transform: "none",
    zIndex: 40,
  },
  displayBrandCluster: {
    alignItems: "center",
    display: "flex",
    gap: "clamp(8px, 1.1vw, 16px)",
    left: "clamp(8px, 1.4vw, 24px)",
    maxWidth: "min(68vw, 1180px)",
    minWidth: 0,
    position: "absolute",
    top: "clamp(2px, 0.7vw, 12px)",
    zIndex: 6,
  },
  displayCornerIcon: {
    display: "block",
    height: "clamp(36px, 6.6vmin, 94px)",
    flex: "0 0 auto",
    objectFit: "contain",
    width: "clamp(36px, 6.6vmin, 94px)",
  },
  topBadgeCluster: {
    alignItems: "center",
    display: "flex",
    gap: "clamp(7px, 1vw, 12px)",
    justifyContent: "flex-end",
    maxWidth: "min(42vw, 640px)",
    position: "absolute",
    right: "clamp(10px, 1.6vw, 28px)",
    top: "clamp(10px, 1.6vw, 28px)",
    zIndex: 6,
  },
  sourceBadge: {
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.62)",
    backdropFilter: "blur(14px) saturate(1.1)",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    borderRadius: 999,
    boxShadow: "0 12px 34px rgba(15, 23, 42, 0.08)",
    boxSizing: "border-box",
    color: "#64748b",
    display: "flex",
    fontSize: "clamp(10px, 1.1vw, 15px)",
    fontWeight: 900,
    gap: 7,
    letterSpacing: "0.08em",
    lineHeight: 1,
    padding: "clamp(7px, 1vh, 10px) clamp(10px, 1.5vw, 16px)",
    flex: "0 0 auto",
    textTransform: "uppercase",
  },
  loopKickerBadge: {
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.62)",
    backdropFilter: "blur(14px) saturate(1.1)",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    borderRadius: 999,
    boxShadow: "0 12px 34px rgba(15, 23, 42, 0.08)",
    boxSizing: "border-box",
    color: "#64748b",
    display: "flex",
    fontSize: "clamp(10px, 1.1vw, 15px)",
    fontWeight: 900,
    gap: 7,
    letterSpacing: "0.08em",
    lineHeight: 1,
    padding: "clamp(7px, 1vh, 10px) clamp(10px, 1.5vw, 16px)",
    position: "absolute",
    right: "clamp(10px, 1.6vw, 28px)",
    bottom: "clamp(10px, 1.6vw, 28px)",
    textTransform: "uppercase",
    zIndex: 4,
  },
  titleSlideTitle: {
    boxSizing: "border-box",
    color: "#000000",
    fontWeight: 400,
    lineHeight: 1.08,
    maxWidth: "96vw",
    overflow: "visible",
    padding: "0.04em 0 0.12em",
    textAlign: "center",
    textOverflow: "clip",
    whiteSpace: "nowrap",
    width: "96vw",
  },
  titleSlideMeta: {
    bottom: "clamp(18px, 4vh, 72px)",
    background: "rgba(255, 255, 255, 0.58)",
    backdropFilter: "blur(16px) saturate(1.12)",
    border: "1px solid rgba(148, 163, 184, 0.28)",
    borderRadius: 999,
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.12)",
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "clamp(18px, min(2.1vw, 4vh), 42px)",
    fontWeight: 400,
    left: "50%",
    lineHeight: 1.08,
    maxWidth: "86vw",
    padding: "clamp(10px, 1.5vh, 18px) clamp(18px, 3vw, 42px)",
    position: "absolute",
    textAlign: "center",
    transform: "translateX(-50%)",
  },
  loopTitle: {
    borderBottom: "1px solid rgba(15, 23, 42, 0.16)",
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "clamp(52px, min(8.6vw, 13vh), 148px)",
    fontWeight: 400,
    lineHeight: 0.98,
    marginTop: 0,
    paddingBottom: "clamp(10px, 1.8vh, 24px)",
    width: "100%",
  },
  loopTitleWelcome: {
    borderBottom: "none",
    paddingBottom: 0,
  },
  loopInfoTitle: {
    borderBottom: "1px solid rgba(15, 23, 42, 0.16)",
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "clamp(48px, min(7.6vw, 11vh), 128px)",
    fontWeight: 400,
    lineHeight: 0.98,
    marginTop: 0,
    paddingBottom: "clamp(10px, 1.8vh, 22px)",
    textAlign: "center",
    textWrap: "balance",
    width: "100%",
  },
  loopInfoTitleHymns: {
    fontSize: "clamp(38px, min(6vw, 8.6vh), 104px)",
    paddingBottom: "clamp(7px, 1.2vh, 16px)",
  },
  loopInfoSubtitle: {
    color: "#334155",
    fontSize: "clamp(20px, min(2.6vw, 4.4vh), 46px)",
    fontWeight: 500,
    lineHeight: 1.12,
    marginTop: "clamp(14px, 2.4vh, 32px)",
    textAlign: "center",
    textWrap: "balance",
  },
  loopInfoBody: {
    color: "#0f172a",
    fontSize: "clamp(24px, min(3.4vw, 5.8vh), 60px)",
    fontWeight: 400,
    lineHeight: 1.18,
    marginTop: "clamp(18px, 3vh, 42px)",
    maxWidth: "min(980px, 84vw)",
    textAlign: "center",
    textWrap: "balance",
    whiteSpace: "pre-line",
  },
  welcomeContent: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "flex-start",
    minHeight: 0,
    overflow: "hidden",
    paddingTop: "clamp(8px, 1.2vh, 18px)",
    width: "100%",
  },
  welcomeContentCentered: {
    flex: "0 1 auto",
    justifyContent: "center",
    paddingBottom: "clamp(28px, 6vh, 84px)",
    paddingTop: 0,
  },
  welcomeTitle: {
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "clamp(76px, min(12vw, 17vh), 214px)",
    fontWeight: 400,
    lineHeight: 0.9,
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "center",
    textWrap: "balance",
  },
  welcomeChurchName: {
    boxSizing: "border-box",
    color: "#000000",
    fontSize: "clamp(30px, min(4.4vw, 7vh), 78px)",
    fontWeight: 400,
    lineHeight: 1.04,
    marginTop: "clamp(18px, 3vh, 42px)",
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "center",
    textWrap: "balance",
  },
  welcomeBody: {
    color: "#000000",
    fontSize: "clamp(18px, min(2vw, 3.6vh), 38px)",
    fontWeight: 400,
    lineHeight: 1.35,
    marginTop: "auto",
    maxWidth: "min(960px, 82vw)",
    paddingBottom: "clamp(10px, 2vh, 30px)",
    textAlign: "center",
  },
  welcomeBodyCentered: {
    marginTop: "clamp(22px, 4vh, 58px)",
    paddingBottom: 0,
  },
  subtitle: {
    fontSize: "clamp(28px, min(5vw, 8vh), 88px)",
    fontWeight: 400,
    lineHeight: 1,
    marginTop: "clamp(16px, 2.8vh, 40px)",
  },
  body: {
    fontSize: "clamp(22px, min(3.2vw, 5.8vh), 58px)",
    fontWeight: 400,
    lineHeight: 1.25,
    marginTop: "clamp(18px, 3vh, 42px)",
    maxWidth: "min(1120px, 92vw)",
    whiteSpace: "pre-line",
  },
  bodyWelcome: {
    color: "#000000",
    fontSize: "clamp(16px, min(2vw, 3.2vh), 34px)",
    fontWeight: 400,
    lineHeight: 1.35,
    marginTop: "auto",
  },
  entryList: {
    display: "grid",
    gap: "clamp(12px, 1.6vh, 20px)",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))",
    marginTop: "clamp(18px, 3vh, 42px)",
    maxWidth: "min(1180px, 90vw)",
    overflow: "hidden",
    width: "100%",
  },
  hymnEntryList: {
    display: "flex",
    flexDirection: "column",
    gap: "clamp(10px, 1.4vh, 18px)",
    marginTop: "clamp(18px, 3vh, 42px)",
    maxWidth: "min(1120px, 88vw)",
    minHeight: 0,
    width: "100%",
  },
  hymnEntryRow: {
    alignItems: "center",
    borderBottom: "1px solid rgba(15, 23, 42, 0.16)",
    display: "grid",
    gap: "clamp(18px, 2.4vw, 38px)",
    gridTemplateColumns: "minmax(110px, 0.24fr) minmax(0, 1fr)",
    minHeight: "clamp(72px, 9vh, 118px)",
    padding: "clamp(8px, 1.1vh, 16px) 0",
    textAlign: "left",
  },
  hymnEntryNumber: {
    color: "#000000",
    fontSize: "clamp(28px, min(4vw, 7vh), 76px)",
    fontWeight: 500,
    lineHeight: 1,
  },
  hymnEntryMain: {
    minWidth: 0,
  },
  hymnEntryTitle: {
    color: "#000000",
    fontSize: "clamp(24px, min(3.2vw, 5.6vh), 60px)",
    fontWeight: 400,
    lineHeight: 1.16,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  hymnEntryLabel: {
    color: "#000000",
    fontSize: "clamp(11px, 1vw, 16px)",
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginTop: "clamp(6px, 0.7vh, 10px)",
    opacity: 0.72,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  announcementList: {
    display: "flex",
    flexDirection: "column",
    gap: "clamp(14px, 1.8vh, 26px)",
    marginTop: "clamp(24px, 3.6vh, 52px)",
    maxHeight: "min(68vh, 760px)",
    maxWidth: "min(1380px, 92vw)",
    overflow: "hidden",
    width: "100%",
  },
  announcementRow: {
    alignItems: "start",
    borderBottom: "1px solid rgba(15, 23, 42, 0.18)",
    display: "grid",
    gap: "clamp(18px, 2.4vw, 38px)",
    gridTemplateColumns: "clamp(54px, 6vw, 92px) minmax(0, 1fr)",
    minHeight: "clamp(74px, 9vh, 116px)",
    padding: "clamp(10px, 1.2vh, 18px) 0 clamp(14px, 1.8vh, 24px)",
    textAlign: "left",
  },
  announcementRowNoNumber: {
    alignItems: "start",
    borderBottom: "1px solid rgba(15, 23, 42, 0.18)",
    display: "grid",
    gap: "clamp(14px, 1.8vw, 28px)",
    gridTemplateColumns: "minmax(0, 1fr)",
    minHeight: "clamp(92px, 10.8vh, 154px)",
    padding: "clamp(12px, 1.45vh, 22px) 0 clamp(18px, 2.2vh, 32px)",
    textAlign: "left",
  },
  announcementNumber: {
    color: "#000000",
    fontSize: "clamp(20px, min(2.6vw, 4.4vh), 46px)",
    fontWeight: 700,
    lineHeight: 1.1,
    opacity: 0.34,
  },
  announcementContent: {
    minWidth: 0,
  },
  announcementTitle: {
    color: "#000000",
    fontSize: "clamp(34px, min(4.1vw, 7vh), 78px)",
    fontWeight: 500,
    lineHeight: 1.04,
    overflowWrap: "anywhere",
  },
  announcementBody: {
    color: "#000000",
    fontSize: "clamp(23px, min(2.45vw, 4.2vh), 46px)",
    fontWeight: 400,
    lineHeight: 1.18,
    marginTop: "clamp(8px, 1vh, 16px)",
    opacity: 0.82,
    overflowWrap: "anywhere",
    whiteSpace: "pre-line",
  },
  announcementMoreText: {
    color: "#000000",
    fontSize: "clamp(18px, 1.7vw, 30px)",
    fontWeight: 800,
    marginTop: "clamp(2px, 0.5vh, 8px)",
    opacity: 0.62,
  },
  entryCard: {
    background: "rgba(255, 255, 255, 0.52)",
    border: "1px solid rgba(15, 23, 42, 0.1)",
    borderRadius: 18,
    boxSizing: "border-box",
    minHeight: "clamp(112px, 14vh, 172px)",
    padding: "clamp(16px, 2.2vh, 28px)",
    paddingBottom: "clamp(34px, 4vh, 52px)",
    position: "relative",
  },
  entryLabel: {
    bottom: "clamp(12px, 1.5vh, 20px)",
    color: "#000000",
    fontSize: "clamp(11px, 1vw, 16px)",
    fontWeight: 900,
    letterSpacing: "0.08em",
    maxWidth: "calc(100% - clamp(32px, 4.4vh, 56px))",
    overflow: "hidden",
    position: "absolute",
    right: "clamp(16px, 2.2vh, 28px)",
    textAlign: "right",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  entryTitle: {
    color: "#000000",
    fontSize: "clamp(22px, min(3vw, 5.2vh), 52px)",
    fontWeight: 400,
    lineHeight: 1.22,
    marginTop: 8,
  },
  entryMeta: {
    color: "#000000",
    fontSize: "clamp(16px, min(1.8vw, 3vh), 30px)",
    fontWeight: 400,
    lineHeight: 1.3,
    marginTop: 8,
  },
  entryBody: {
    color: "#000000",
    fontSize: "clamp(16px, min(1.8vw, 3vh), 30px)",
    fontWeight: 400,
    lineHeight: 1.4,
    marginTop: 10,
    whiteSpace: "pre-line",
  },
  moreText: {
    color: "#000000",
    fontSize: "clamp(14px, 1.3vw, 20px)",
    fontWeight: 800,
    marginTop: 4,
  },
  previewEntryList: {
    display: "grid",
    gap: 10,
    marginTop: 18,
    width: "100%",
  },
  previewHymnEntryList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 18,
    width: "100%",
  },
  previewHymnEntryRow: {
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "58px minmax(0, 1fr)",
    padding: "8px 0",
    textAlign: "left",
  },
  previewHymnEntryNumber: {
    color: "#000000",
    fontSize: 18,
    fontWeight: 850,
  },
  previewHymnEntryMain: {
    minWidth: 0,
  },
  previewHymnEntryTitle: {
    color: "#000000",
    fontSize: 14,
    fontWeight: 850,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewHymnEntryLabel: {
    color: "#000000",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginTop: 2,
    opacity: 0.7,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  previewAnnouncementList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 18,
    width: "100%",
  },
  previewAnnouncementRow: {
    borderTop: "1px solid #e2e8f0",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "30px minmax(0, 1fr)",
    paddingTop: 9,
    textAlign: "left",
  },
  previewAnnouncementRowNoNumber: {
    borderTop: "1px solid #e2e8f0",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "minmax(0, 1fr)",
    paddingTop: 9,
    textAlign: "left",
  },
  previewAnnouncementNumber: {
    color: "#000000",
    fontSize: 12,
    fontWeight: 900,
    opacity: 0.36,
    paddingTop: 2,
  },
  previewAnnouncementTitle: {
    color: "#000000",
    fontSize: "clamp(14px, 1.4vw, 20px)",
    fontWeight: 850,
    lineHeight: 1.2,
  },
  previewAnnouncementBody: {
    color: "#000000",
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 4,
    opacity: 0.74,
    whiteSpace: "pre-line",
  },
  previewAnnouncementMoreText: {
    color: "#000000",
    fontSize: 12,
    fontWeight: 750,
    opacity: 0.64,
  },
  previewEntryCard: {
    borderTop: "1px solid #e2e8f0",
    paddingBottom: 22,
    paddingTop: 10,
    position: "relative",
  },
  previewEntryLabel: {
    bottom: 2,
    color: "#000000",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.08em",
    maxWidth: "80%",
    overflow: "hidden",
    position: "absolute",
    right: 0,
    textAlign: "right",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  previewEntryTitle: {
    color: "#000000",
    fontSize: "clamp(14px, 1.4vw, 20px)",
    fontWeight: 800,
    lineHeight: 1.2,
    marginTop: 4,
  },
  previewEntryMeta: {
    color: "#000000",
    fontSize: 12,
    fontWeight: 700,
    marginTop: 4,
  },
  previewEntryBody: {
    color: "#000000",
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 6,
    whiteSpace: "pre-line",
  },
  previewMoreText: {
    color: "#000000",
    fontSize: 12,
    fontWeight: 700,
  },
  verseNumber: {
    fontSize: "0.62em",
    fontWeight: 800,
    marginRight: 10,
    opacity: 0.55,
    verticalAlign: "top",
  },
  logo: {
    height: "clamp(110px, 18vh, 220px)",
    marginBottom: "clamp(20px, 4vh, 46px)",
    objectFit: "contain",
    width: "min(460px, 58vw)",
  },
  emptyCard: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 10,
    justifyContent: "center",
    padding: 24,
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: 28,
    fontWeight: 900,
  },
  emptyText: {
    color: "#000000",
    fontSize: 16,
    lineHeight: 1.6,
    maxWidth: 520,
  },
};
